import getServerUrl from '../../common-js/settings';
import getLoginUrl from '../../common-js/getLoginUrl';
import getAuthorizationHeader from '../../common-js/authorizationHeader';
import { getBlankPresentation, getMathJournalBookEn, getMathJournalBookEs, getMathJournalSentencesEn, getMathJournalSentencesEs } from '../../common-js/DynamicData';

export default function ($log, $http, $timeout) {
    let isLoginInProgress = false;

    async function usePowerPointAPI(textBoxString) {
        await PowerPoint.run(async (context) => {
            var presentation = context.presentation;
            var selectedSlide = presentation.getSelectedSlides().getItemAt(0);
            selectedSlide.load("id");
            await context.sync();

            const shapes = selectedSlide.shapes;
            const textbox = shapes.addTextBox(textBoxString);
            textbox.left = 50;
            textbox.top = 50;
            // textbox.height = 200;
            textbox.width = 250;
            textbox.name = "Textbox";
            textbox.textFrame.wordWrap = true;
            if (PowerPoint.ShapeAutoSize && PowerPoint.ShapeAutoSize.textToFitShape) {
                textbox.textFrame.autoSizeSetting = PowerPoint.ShapeAutoSize.textToFitShape;
            }
            await context.sync();
        }).catch((error) => {
            console.error("PowerPoint API error:", error);
        });
    }

    function useOfficeContextAPI(textBoxString) {
        Office.context.document.getSelectedDataAsync(
            Office.CoercionType.SlideRange,
            async function (asyncResult) {
                if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                    let slideIndex = asyncResult.value.slides[0].index;
                    console.log("Adding text to slide:", slideIndex);

                    Office.context.document.setSelectedDataAsync(
                        textBoxString,
                        { coercionType: Office.CoercionType.Text },
                        function (result) {
                            if (result.status === Office.AsyncResultStatus.Failed) {
                                console.error("Error inserting text:", result.error.message);
                            } else {
                                console.log("Text added successfully to slide", slideIndex);
                            }
                        }
                    );
                } else {
                    console.error("Error:", asyncResult.error.message);
                }
            }
        );
    }

    /**
     * Adds a new blank slide to the current PowerPoint presentation.
     * Iterates through slide masters and layouts to find and add a slide with a "Blank" layout.
     * Resolves with the current slide count upon successful addition.
     * Rejects with an error message in case of failure during the slide addition process.
     * 
     * Made for Local PowerPoint Online
     */
    async function addNewSlide() {
        return new Promise(async (resolve, reject) => {
            try {
                await PowerPoint.run(async function (context) {
                    let slides = context.presentation.slides;
                    slides.load("items");
                    await context.sync();

                    let slideCount = slides.items.length;
                    let newSlideId = null;

                    try {
                        const slideMasters = context.presentation.slideMasters.load("id, name, layouts/items/name, layouts/items/id, layouts/items/type");
                        await context.sync();

                        // Log the name and ID of each slide master.
                        for (let i = 0; i < slideMasters.items.length; i++) {
                            // console.log("Master name: " + slideMasters.items[i].name);
                            // console.log("Master ID: " + slideMasters.items[i].id);
                            // console.log("Master layouts count: " + slideMasters.items[i].layouts);

                            // Log the name and ID of each slide layout in the slide master.
                            const layoutsInMaster = slideMasters.items[i].layouts;
                            for (let j = 0; j < layoutsInMaster.items.length; j++) {
                                console.log("Layout name: " + layoutsInMaster.items[j].name + " Layout ID: " + layoutsInMaster.items[j].id + " Layout Type: " + layoutsInMaster.items[j].type);
                                if (layoutsInMaster.items[j].type === "Blank" || layoutsInMaster.items[j].name.toLowerCase().includes("blank")) {
                                    context.presentation.slides.add({
                                        slideMasterId: slideMasters.items[i].id,
                                        layoutId: layoutsInMaster.items[j].id
                                    });
                                    await context.sync();

                                    const newSlide = context.presentation.slides.getItemAt(slideCount);
                                    newSlide.load("id");
                                    await context.sync();
                                    newSlideId = newSlide.id;
                                    break;
                                }
                            }
                            if (newSlideId) break;
                        }
                    } catch (masterError) {
                        console.warn("Could not load slide masters or find blank layout:", masterError);
                    }

                    if (!newSlideId) {
                        console.log("No blank layout found or slide masters load failed, adding default slide");
                        context.presentation.slides.add();
                        await context.sync();

                        const newSlide = context.presentation.slides.getItemAt(slideCount);
                        newSlide.load("id");

                        // Clear the shapes (textboxes, titles, etc) from the default slide to make it blank
                        const shapes = newSlide.shapes;
                        shapes.load("items");
                        await context.sync();

                        shapes.items.forEach((shape) => {
                            shape.delete();
                        });
                        await context.sync();

                        newSlideId = newSlide.id;
                    }

                    resolve({ slideCount, slideId: newSlideId });
                });
            } catch (error) {
                console.error("Error adding blank slide:", error);
                reject(error);
            }
        });
    }

    /**
     * Adds a new slide to the end of the current PowerPoint presentation.
     * Loads the slides collection and determines the current slide count.
     * Inserts a new slide and deletes all shapes from it to ensure it's blank.
     * Resolves with the slide count upon successful addition and deletion of shapes.
     * Rejects with an error message in case of failure during the slide addition process.
     * 
     * Made for Local PowerPoint Desktop
     */
    async function addNewSlideV2() {
        return new Promise(async (resolve, reject) => {
            try {
                await PowerPoint.run(async function (context) {
                    let slides = context.presentation.slides;
                    slides.load("items");
                    await context.sync();

                    let slideCount = slides.items.length;

                    context.presentation.load("slides");
                    await context.sync();

                    const targetSlide = slides.getItemAt(slideCount - 1);
                    targetSlide.load("id");
                    await context.sync();

                    const targetSlideId = targetSlide.id;

                    // console.log(targetSlideId);

                    context.presentation.insertSlidesFromBase64(getBlankPresentation(), {
                        targetSlideId: targetSlideId
                    });

                    await context.sync();

                    const newSlide = context.presentation.slides.getItemAt(slideCount);
                    newSlide.load("id");
                    await context.sync();
                    const newSlideId = newSlide.id;

                    resolve({ slideCount, slideId: newSlideId });
                });
            } catch (error) {
                console.error("Error adding blank slide:", error);
                reject(error);
            }
        });
    }

    async function deleteSlide() {
        await PowerPoint.run(async function (context) {

            // The slide index is zero-based. 
            const slide = context.presentation.slides.getItemAt(3);
            slide.delete();

            await context.sync();
        });
    }

    const addTextBoxToSlide = async (textBoxString) => {
        const promise = new Promise(async (resolve, reject) => {
            try {
                $log.info('Adding text box to slide', textBoxString.text);
                await PowerPoint.run(async (context) => {
                    var presentation = context.presentation;
                    var selectedSlide = presentation.getSelectedSlides().getItemAt(0);
                    selectedSlide.load("id");
                    await context.sync();

                    const shapes = selectedSlide.shapes;
                    const textbox = shapes.addTextBox(textBoxString);
                    textbox.left = 50;
                    textbox.top = 50;
                    // textbox.height = 200;
                    textbox.width = 250;
                    textbox.name = "Textbox";
                    textbox.textFrame.wordWrap = true;
                    if (PowerPoint.ShapeAutoSize && PowerPoint.ShapeAutoSize.textToFitShape) {
                        textbox.textFrame.autoSizeSetting = PowerPoint.ShapeAutoSize.textToFitShape;
                    }
                    await context.sync();

                    resolve("Success");
                }).catch((error) => {
                    console.error("PowerPoint API error:", error);
                });
            } catch (error) {
                $log.info(error);
                reject(error);
            }
        });
        return promise;
    };

    const addTextBoxPairToSlide = async (question, sentences) => {
        const promise = new Promise(async (resolve, reject) => {
            try {
                await PowerPoint.run(async (context) => {
                    const presentation = context.presentation;
                    const selectedSlide = presentation.getSelectedSlides().getItemAt(0);
                    selectedSlide.load("id");
                    await context.sync();

                    const shapes = selectedSlide.shapes;

                    // Normalize inputs to strings
                    const questionText = typeof question === "string" ? question : (question && question.text) ? question.text : "";
                    const sentenceList = Array.isArray(sentences) ? sentences : [];

                    // Layout constants
                    const left = 50;
                    const initialTop = 50;
                    const width = 250;
                    const gap = 12; // vertical gap between boxes

                    // ---- Add Question box ----
                    const questionTextbox = shapes.addTextBox(questionText);
                    questionTextbox.left = left;
                    questionTextbox.top = initialTop;
                    questionTextbox.width = width;
                    questionTextbox.name = "QuestionTextbox";

                    // Load textFrame before using autoSizeSetting
                    questionTextbox.load("textFrame");
                    await context.sync();

                    // Let the shape resize to fit the text so height is accurate
                    if (questionTextbox.textFrame && questionTextbox.textFrame.autoSizeSetting !== undefined) {
                        questionTextbox.textFrame.autoSizeSetting = "AutoSizeShapeToFitText";
                    }
                    if (questionTextbox.textFrame && questionTextbox.textFrame.wordWrap !== undefined) {
                        questionTextbox.textFrame.wordWrap = true;
                    }
                    await context.sync();

                    // Load height to compute next top
                    questionTextbox.load("height, top");
                    await context.sync();

                    let currentTop = questionTextbox.top + questionTextbox.height + gap;

                    // ---- Add sentence boxes stacked one after another ----
                    const createdSentenceIds = [];
                    for (let i = 0; i < sentenceList.length; i++) {
                        const sRaw = sentenceList[i];
                        const sentenceText = typeof sRaw === "string" ? sRaw : (sRaw && sRaw.text) ? sRaw.text : String(sRaw ?? "");

                        const sentenceTextbox = shapes.addTextBox(sentenceText);
                        sentenceTextbox.left = left;
                        sentenceTextbox.top = currentTop;
                        sentenceTextbox.width = width;
                        sentenceTextbox.name = `SentenceTextbox_${i}`;

                        sentenceTextbox.load("textFrame");
                        await context.sync();

                        if (sentenceTextbox.textFrame && sentenceTextbox.textFrame.autoSizeSetting !== undefined) {
                            sentenceTextbox.textFrame.autoSizeSetting = "AutoSizeShapeToFitText";
                        }
                        if (sentenceTextbox.textFrame && sentenceTextbox.textFrame.wordWrap !== undefined) {
                            sentenceTextbox.textFrame.wordWrap = true;
                        }
                        await context.sync();

                        // Measure actual height and advance the cursor
                        sentenceTextbox.load("height, top");
                        await context.sync();

                        currentTop = sentenceTextbox.top + sentenceTextbox.height + gap;
                        createdSentenceIds.push(sentenceTextbox.name);
                    }

                    resolve("Success");
                }).catch((error) => {
                    console.error("PowerPoint API error:", error);
                    reject(error);
                });
            } catch (error) {
                $log.info(error);
                reject(error);
            }
        });
        return promise;
    };

    async function getBase64FromUrl(url) {
        let response = await fetch(url);
        let blob = await response.blob();
        return new Promise((resolve) => {
            let reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onloadend = () => {
                let base64String = reader.result.split(',')[1]; // Remove metadata prefix
                resolve(base64String);
            };
        });
    };

    function getImageSizeFromBase64(base64String) {
        return new Promise((resolve, reject) => {
            let img = new Image();
            img.onload = function () {
                console.log({ 'width': img.width, 'height': img.height })
                resolve({ width: img.width, height: img.height });
            };
            img.onerror = reject;
            img.src = base64String;

        });
    }

    function getSelectedSlideIndex(position) {
        return new OfficeExtension.Promise < number > (function (resolve, reject) {
            Office.context.document.getSelectedDataAsync(Office.CoercionType.SlideRange, function (asyncResult) {
                try {
                    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                        reject(console.error(asyncResult.error.message));
                    } else {
                        resolve(asyncResult.value.slides[position].index);
                    }
                }
                catch (error) {
                    reject(console.log(error));
                }
            });
        });
    }

    const selectSlideAndAddBackgroundImage = async (slideTarget, imageUrl, baseWidth, baseHeight, context, targetType = Office.GoToType.Index) => {
        console.log({ slideTarget, imageUrl, baseWidth, baseHeight, context, targetType });
        const promise = new Promise((resolve, reject) => {
            Office.context.document.goToByIdAsync(slideTarget, targetType, async (asyncResult) => {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    console.log("Error:", asyncResult.error.message);
                    reject(asyncResult.error.message);
                } else {
                    console.log("Slide changed successfully.");

                    await context.sync();

                    // adding image to slide
                    let base64Image = await getBase64FromUrl(imageUrl);
                    Office.context.document.setSelectedDataAsync(
                        base64Image,
                        {
                            coercionType: Office.CoercionType.Image,
                            imageHeight: baseHeight,
                            imageWidth: baseWidth,
                            imageTop: 0,
                            imageLeft: 0
                        },
                        (asyncResult) => {
                            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                reject(asyncResult.error.message);
                            } else {
                                resolve(slideTarget);
                            }
                        }
                    );
                }
            })
        });

        return promise
    };

    const addBackgroundImageToSlide = async (imageUrl, baseWidth, baseHeight) => {
        const addBackgroundImageToSlidePromise = new Promise(async (resolve, reject) => {
            await PowerPoint.run(async function (context) {
                addNewSlide().then(async (result) => {
                    const slideCount = result.slideCount;
                    const slideId = result.slideId;

                    console.log("New blank slide created successfully", slideCount);
                    console.log("New blank slide created successfully with ID", slideId);

                    await PowerPoint.run(async (context) => {
                        if (slideId) {
                            selectSlideAndAddBackgroundImage(slideId, imageUrl, baseWidth, baseHeight, context, Office.GoToType.Slide).then(async (success) => {
                                console.log("selectSlideAndAddBackgroundImage by ID", success);
                                resolve(slideCount);
                            }).catch((error) => {
                                selectSlideAndAddBackgroundImage(slideCount + 1, imageUrl, baseWidth, baseHeight, context).then(async (success) => {
                                    console.log("selectSlideAndAddBackgroundImage", success);
                                    resolve(slideCount);
                                }).catch((error) => {
                                    selectSlideAndAddBackgroundImage(slideCount, imageUrl, baseWidth, baseHeight, context).then(() => resolve(slideCount)).catch(reject);
                                });
                            });
                        } else {
                            selectSlideAndAddBackgroundImage(slideCount + 1, imageUrl, baseWidth, baseHeight, context).then(async (success) => {
                                console.log("selectSlideAndAddBackgroundImage", success);
                                resolve(slideCount);
                            }).catch((error) => {
                                selectSlideAndAddBackgroundImage(slideCount, imageUrl, baseWidth, baseHeight, context).then(() => resolve(slideCount)).catch(reject);
                            });
                        }
                    });
                });
            });
        });

        return addBackgroundImageToSlidePromise;
    };

    const addBackgroundImageToSlideV2 = async (imageUrl, baseWidth, baseHeight) => {
        const addBackgroundImageToSlidePromise = new Promise(async (resolve, reject) => {
            await PowerPoint.run(async function (context) {
                await addNewSlideV2().then(async (result) => {
                    console.log("New blank slide created successfully using addBackgroundImageToSlideV2.", result.slideCount);

                    const slideCount = result.slideCount;
                    const slideId = result.slideId;

                    await PowerPoint.run(async (context) => {
                        if (slideId) {
                            selectSlideAndAddBackgroundImage(slideId, imageUrl, baseWidth, baseHeight, context, Office.GoToType.Slide).then(async (success) => {
                                console.log("selectSlideAndAddBackgroundImage by ID", success);
                                resolve(slideCount);
                            }).catch((error) => {
                                selectSlideAndAddBackgroundImage(slideCount + 1, imageUrl, baseWidth, baseHeight, context).then(async (success) => {
                                    console.log("selectSlideAndAddBackgroundImage", success);
                                    resolve(slideCount);
                                }).catch((error) => {
                                    selectSlideAndAddBackgroundImage(slideCount, imageUrl, baseWidth, baseHeight, context).then(() => resolve(slideCount)).catch(reject);
                                });
                            });
                        } else {
                            selectSlideAndAddBackgroundImage(slideCount + 1, imageUrl, baseWidth, baseHeight, context).then(async (success) => {
                                console.log("selectSlideAndAddBackgroundImage", success);
                                resolve(slideCount);
                            }).catch((error) => {
                                selectSlideAndAddBackgroundImage(slideCount, imageUrl, baseWidth, baseHeight, context).then(() => resolve(slideCount)).catch(reject);
                            });
                        }
                    });
                });
            });
        });

        return addBackgroundImageToSlidePromise;
    };

    const addBackgroundImageToSlideV3 = async (imageUrl, baseWidth, baseHeight) => {
        const addBackgroundImageToSlidePromise = new Promise(async (resolve, reject) => {
            try {
                if (Office.context.requirements.isSetSupported("PowerPointApi", "1.10")) {
                    console.log("PowerPointApi 1.10 is supported. Setting slide background natively.");
                    await addNewSlideV2().then(async (result) => {
                        const slideCount = result.slideCount;
                        const slideId = result.slideId;
                        console.log("New blank slide created successfully using V3.", slideCount);

                        const base64Image = await getBase64FromUrl(imageUrl);

                        await PowerPoint.run(async (context) => {
                            let slide;
                            const numId = Number(slideId);
                            if (slideId && typeof slideId === "string" && isNaN(numId)) {
                                slide = context.presentation.slides.getItem(slideId);
                            } else if (!isNaN(numId) && slideId !== null && slideId !== undefined && slideId !== "") {
                                slide = context.presentation.slides.getItemAt(numId);
                            } else {
                                slide = context.presentation.slides.getItemAt(slideCount);
                            }

                            slide.background.fill.setPictureOrTextureFill({
                                imageBase64: base64Image,
                                transparency: 0.0
                            });

                            await context.sync();
                            console.log("Slide background set successfully using setPictureOrTextureFill.");
                            resolve(slideCount);
                        });
                    }).catch(reject);
                } else {
                    console.log("PowerPointApi 1.10 is not supported. Falling back to addBackgroundImageToSlideV2.");
                    addBackgroundImageToSlideV2(imageUrl, baseWidth, baseHeight)
                        .then(resolve)
                        .catch(reject);
                }
            } catch (error) {
                console.error("Error in addBackgroundImageToSlideV3:", error);
                reject(error);
            }
        });

        return addBackgroundImageToSlidePromise;
    };

    const getPresentationSlideSize = async () => {
        if (Office.context.requirements.isSetSupported("PowerPointApi", "1.10")) {
            try {
                return await PowerPoint.run(async (context) => {
                    const pageSetup = context.presentation.pageSetup;
                    pageSetup.load(["slideWidth", "slideHeight"]);
                    await context.sync();
                    return {
                        width: pageSetup.slideWidth,
                        height: pageSetup.slideHeight
                    };
                });
            } catch (error) {
                console.error("Error fetching presentation slide size:", error);
                return null;
            }
        }
        return null;
    };


    const getTextBoxPositions = async () => {
        let slides = [];
        let textBoxes = [];
        return await PowerPoint.run(async (context) => {
            var presentation = context.presentation;
            // var selectedSlide = presentation.getSelectedSlides().getItemAt(0);
            var selectedSlides = presentation.getSelectedSlides();
            context.load(selectedSlides, "items"); // Load the slide collection
            await context.sync();

            let count = selectedSlides.items.length;
            for (var j = 0; j < count; j++) {
                const selectedSlide = selectedSlides.getItemAt(j);
                await context.sync();

                // Get shape count
                var shapeCountObj = selectedSlide.shapes.getCount();
                await context.sync();

                const shapeCount = shapeCountObj.value;
                console.log(shapeCount);
                // Log text box properties
                for (var i = 0; i < shapeCount; i++) {
                    const shape = selectedSlide.shapes.getItemAt(i);
                    shape.load(["textFrame/textRange/text",
                        "left",
                        "top",
                        "width",
                        "height",
                        "lineFormat/color",
                        "lineFormat/weight",
                        "lineFormat/dashStyle",
                        "textFrame/textRange/paragraphFormat/horizontalAlignment",
                        "textFrame/textRange/font/name",
                        "textFrame/textRange/font/size",
                        "textFrame/textRange/font/bold",
                        "textFrame/textRange/font/italic",
                        "textFrame/textRange/font/color",
                    ]);
                    await context.sync();

                    textBoxes.push({
                        text: shape.textFrame.textRange.text,
                        left: shape.left,
                        top: shape.top,
                        width: shape.width,
                        height: shape.height,
                        borderColor: shape.lineFormat.color,
                        borderWidth: shape.lineFormat.weight,
                        borderStyle: shape.lineFormat.dashStyle,
                        alignment: shape.textFrame.textRange.paragraphFormat.horizontalAlignment,
                        fontName: shape.textFrame.textRange.font.name,
                        fontSize: shape.textFrame.textRange.font.size,
                        bold: shape.textFrame.textRange.font.bold,
                        italic: shape.textFrame.textRange.font.italic,
                        color: shape.textFrame.textRange.font.color
                    });
                }

                slides.push({
                    textbox: textBoxes
                });

                textBoxes = [];

                if (j + 1 == count) {
                    return slides;
                }
            }
        }).catch((error) => {
            console.error("PowerPoint API error:", error);
        });
    };

    async function addTextBoxWithStyles(texboxObj) {
        await PowerPoint.run(async (context) => {
            var presentation = context.presentation;
            var selectedSlide = presentation.getSelectedSlides().getItemAt(0);
            selectedSlide.load("id");
            await context.sync();

            const shapes = selectedSlide.shapes;
            const textbox = shapes.addTextBox(texboxObj.text);
            textbox.left = texboxObj.left;
            textbox.top = texboxObj.top;
            textbox.height = texboxObj.height;
            if (texboxObj.text !== '') {
                textbox.width = texboxObj.width + 25;
            } else {
                textbox.width = texboxObj.width;
            }
            if (texboxObj.borderColor !== '') {
                textbox.lineFormat.color = texboxObj.borderColor;
                if (texboxObj.borderWidth > 0) {
                    textbox.lineFormat.weight = texboxObj.borderWidth;
                }

                textbox.lineFormat.dashStyle = texboxObj.borderStyle;
            }
            if (texboxObj.fontName !== '') textbox.textFrame.textRange.font.name = texboxObj.fontName;
            textbox.textFrame.textRange.font.size = texboxObj.fontSize;
            textbox.textFrame.textRange.font.bold = texboxObj.bold;
            textbox.textFrame.textRange.font.italic = texboxObj.italic;
            if (texboxObj.color !== '') textbox.textFrame.textRange.font.color = texboxObj.color;
            textbox.textFrame.textRange.paragraphFormat.horizontalAlignment = texboxObj.alignment;
            textbox.textFrame.wordWrap = true;
            if (PowerPoint.ShapeAutoSize && PowerPoint.ShapeAutoSize.textToFitShape) {
                textbox.textFrame.autoSizeSetting = PowerPoint.ShapeAutoSize.textToFitShape;
            }
            await context.sync();
        }).catch((error) => {
            console.error("PowerPoint API error:", error);
        });
    }

    async function addTextBoxesWithStyles(texboxArray, textboxXScale = 1, slideIndex = 0, textboxYScale = 1) {
        console.log('addTextBoxesWithStyles - slideIndex:', slideIndex, 'type:', typeof slideIndex, "slideIndex:::", slideIndex);
        const addTextBoxesWithStylesPromise = new Promise(async (resolve, reject) => {
            await PowerPoint.run(async (context) => {
                var presentation = context.presentation;
                // var selectedSlide = presentation.getSelectedSlides().getItemAt(0);
                // selectedSlide.load("id");

                let selectedSlide;
                const numIndex = Number(slideIndex);
                if (slideIndex !== null && slideIndex !== undefined && slideIndex !== "" && !isNaN(numIndex)) {
                    selectedSlide = presentation.slides.getItemAt(numIndex);
                } else if (typeof slideIndex === "string" && slideIndex.trim() !== "") {
                    selectedSlide = presentation.slides.getItem(slideIndex);
                } else {
                    selectedSlide = presentation.getSelectedSlides().getItemAt(0);
                }
                context.load(selectedSlide, "id");
                context.load(selectedSlide.shapes, "items");
                await context.sync();

                const shapes = selectedSlide.shapes;
                for (const texboxObj of texboxArray) {
                    const textbox = shapes.addTextBox(texboxObj.text);
                    textbox.left = texboxObj.left * textboxXScale; // to scale the left according to aspect ratio of base image
                    textbox.top = texboxObj.top * textboxYScale;
                    textbox.height = texboxObj.height * textboxYScale;
                    texboxObj.width = texboxObj.width * textboxXScale; // to scale the width according to aspect ratio of base image
                    if (texboxObj.text !== '') {
                        textbox.width = texboxObj.width + 25;
                    } else {
                        textbox.width = texboxObj.width;
                    }
                    if (texboxObj.borderColor !== '') {
                        textbox.lineFormat.color = texboxObj.borderColor;
                        if (texboxObj.borderWidth > 0) {
                            textbox.lineFormat.weight = texboxObj.borderWidth;
                        }

                        textbox.lineFormat.dashStyle = texboxObj.borderStyle;
                    }
                    if (texboxObj.fontName !== '') textbox.textFrame.textRange.font.name = texboxObj.fontName;
                    textbox.textFrame.textRange.font.size = texboxObj.fontSize;
                    textbox.textFrame.textRange.font.bold = texboxObj.bold;
                    textbox.textFrame.textRange.font.italic = texboxObj.italic;
                    if (texboxObj.color !== '') textbox.textFrame.textRange.font.color = texboxObj.color;
                    textbox.textFrame.textRange.paragraphFormat.horizontalAlignment = texboxObj.alignment;
                    textbox.textFrame.wordWrap = true;
                    if (PowerPoint.ShapeAutoSize && PowerPoint.ShapeAutoSize.textToFitShape) {
                        textbox.textFrame.autoSizeSetting = PowerPoint.ShapeAutoSize.textToFitShape;
                    }

                    await context.sync();
                };

                const platform = (Office && Office.context && Office.context.diagnostics && Office.context.diagnostics.platform) ||
                    (Office && Office.context && Office.context.platform);
                console.log(platform);

                //"OfficeOnline" is for browser
                if (platform === "PC" || platform === "Mac") {
                    try {
                        const tempTextBox = shapes.addTextBox("");
                        await context.sync();

                        tempTextBox.delete();
                        await context.sync();
                    } catch (error) {
                        console.info("Error while creating temporary box to update UI");
                    }
                }

                console.log("All text boxes added successfully.");
                resolve("Success");
            }).catch((error) => {
                console.error("PowerPoint API error:", error);
                reject(error);
            });
        });

        return addTextBoxesWithStylesPromise;
    }

    function getAccessToken() {
        if (localStorage.getItem('drawp-serverToken') !== null) {
            return localStorage.getItem('drawp-serverToken');
        }
        return "";
    }

    function getActiveUserEmail() {
        const userEmail = document.getElementById('userEmail').innerHTML;
        if (userEmail !== '' && userEmail !== 'undefined' && userEmail !== 'null') {
            return userEmail;
        } else if (localStorage.getItem('drawp-userEmail') !== null) {
            return localStorage.getItem('drawp-userEmail');
        }

        return "guest@moondropapps.com";
    }

    const getVelazquezMathJournalGraphics = (language, serverToken = getAccessToken()) => {
        let bookId = '5f8fdefd618072070fc2b993';

        if (language === 'es') {
            bookId = '5f8fdefd618072070fc2b994';
        }
        const velazquezMathJournalGraphicsPromise = new Promise((resolve, reject) => {

            const url = `${getServerUrl()}api/v2/google-addon/book/`;

            const myHeaders = getAuthorizationHeader(serverToken);

            const requestData = {
                email: getActiveUserEmail(),
                page: 1,
                per_page: 40,
                book_id: bookId,
                domain: 'microsoft'
            };

            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                }, function (error) {
                    reject(error);
                });
        });
        return velazquezMathJournalGraphicsPromise;
    };

    const getVelazquezMathJournalFrames = (language, level, serverToken = getAccessToken()) => {
        let bookId = '5f8fdefd618072070fc2b993';

        if (language === 'es') {
            bookId = '5f8fdefd618072070fc2b994';
        }
        const velazquezMathJournalFramesPromise = new Promise((resolve, reject) => {

            const url = `${getServerUrl()}api/v2/google-addon/book/sentence-frames/`;

            const myHeaders = getAuthorizationHeader(serverToken);

            const requestData = {
                email: getActiveUserEmail(),
                page: 1,
                per_page: 100,
                book_id: bookId,
                level,
                domain: 'microsoft'
            };

            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return velazquezMathJournalFramesPromise;
    };

    const addVelazquezSlides = (presentationId, excludedIndexs, velazquez, destIndex, language, width, height, textboxXScale, textboxYScale) => {
        const addVelazquezSlidesPromise = new Promise(async (resolve, reject) => {
            if (velazquez.slide_count > 0) {

                //to handle case for staging and production server id difference
                let velazquezId = velazquez.id;
                if (velazquez.id == '6461f04999f5e07305dd5c73' && language === 'en') {
                    velazquezId = '6466046a5bde52201936a024';
                } else if (velazquez.id == '6461f04999f5e07305dd5c74' && language === 'es') {
                    velazquezId = '6466046a5bde52201936a025';
                } else if (velazquez.id == '6461f04999f5e07305dd5c75' && language === 'en') {
                    velazquezId = '6466046a5bde52201936a026'
                } else if (velazquez.id == '6461f04999f5e07305dd5c76' && language === 'es') {
                    velazquezId = '6466046a5bde52201936a027'
                }

                let textBoxesData = null;
                if (language === 'es') {
                    textBoxesData = getMathJournalBookEs().sections.filter(section => section.id === velazquezId);
                }
                textBoxesData = getMathJournalBookEn().sections.filter(section => section.id === velazquezId);

                // console.log("textBoxesData", textBoxesData);

                for (let i = 0; i < velazquez.slide_count; i++) {
                    const filePath = `assets/vpmath/${velazquezId}_${i + 1}_${language}.jpg`;
                    await addBackgroundImageToSlideV2(filePath, width, height).then(async (response) => {
                        console.log('addBackgroundImageToSlideV2', response);

                        if (textBoxesData.length > 0) {
                            await addTextBoxesWithStyles(textBoxesData[0].textboxes[i].textbox, textboxXScale, response, textboxYScale);
                        }
                    }).catch((error) => {
                        console.log(error);
                    });
                }
                resolve("Success");
            } else {
                reject("No slides found");
            }
        });

        return addVelazquezSlidesPromise;
    };

    const insertQuestionSet = (textData, audioUrl, addAudioBol, presentationId, destIndex, serverToken) => {
        const insertQuestionSetPromise = new Promise((resolve, reject) => {
            addTextBoxToSlide(textData);
            resolve("Success");
        });

        return insertQuestionSetPromise;
    };

    const openLocalPowerpointLoginDialog = () => {
        const authUrl = getLoginUrl() + "commands.html";

        Office.context.ui.displayDialogAsync(
            authUrl,
            { height: 40, width: 35, displayInIframe: false },
            function (result) {
                console.log(result.status);

                if (result.status === Office.AsyncResultStatus.Failed) {
                    console.error("Dialog failed to open:", result.error);
                    return;
                }

                if (result.status == 'succeeded') {
                    console.log("Dialog opened successfully");

                    let authDialog = result.value;

                    authDialog.addEventHandler(Office.EventType.DialogMessageReceived, function (arg) {
                        console.log("DialogMessageReceived", arg);
                        const messageData = JSON.parse(arg.message);
                        // console.log(messageData.accessToken);
                        // console.log(messageData.account.username);

                        const tokenExchangeUrl = `${getServerUrl()}api/v2/addin/token/microsoft/`;
                        $http.post(tokenExchangeUrl, { access_token: messageData.accessToken })
                            .then(function (response) {
                                console.log("DRF token response:", response.data);
                                const token = response.data.token || response.data.key || response.data.access_token || response.data.accessToken || response.data.drawp_serverToken;
                                if (token) {
                                    localStorage.setItem("drawp-serverToken", token);
                                    document.getElementById('serverToken').innerHTML = token;
                                } else {
                                    console.error("No token found in response", response.data);
                                    localStorage.setItem("drawp-serverToken", messageData.accessToken);
                                    document.getElementById('serverToken').innerHTML = messageData.accessToken;
                                }
                                localStorage.setItem("drawp-userEmail", messageData.account.username);
                                localStorage.setItem("drawp-login-type", "microsoft");
                                document.getElementById('userEmail').innerHTML = messageData.account.username;
                                authDialog.close();
                            }, function (error) {
                                console.error("Error exchanging microsoft token:", error);
                                // Fallback to raw access token so it doesn't break completely
                                localStorage.setItem("drawp-serverToken", messageData.accessToken);
                                localStorage.setItem("drawp-userEmail", messageData.account.username);
                                localStorage.setItem("drawp-login-type", "microsoft");
                                document.getElementById('userEmail').innerHTML = messageData.account.username;
                                document.getElementById('serverToken').innerHTML = messageData.accessToken;
                                authDialog.close();
                            });
                    });

                    // Add event handler for dialog closing
                    authDialog.addEventHandler(Office.EventType.DialogEventReceived, function (arg) {
                        console.log("DialogEventReceived", arg);
                    });
                }
            }
        );
    }

    const openLoginDialog = async () => {
        if (isLoginInProgress) {
            console.warn("Login is already in progress.");
            return;
        }

        if (Office.context.requirements.isSetSupported("PowerPointApi", "1.1")) {
            isLoginInProgress = true;
            console.log(getLoginUrl() + "commands.html");
            const msalConfig = {
                auth: {
                    clientId: "a6d141c3-a5e5-4a55-972d-92754b658f8e", // Your client ID from manifest
                    authority: "https://login.microsoftonline.com/common",
                    redirectUri: getLoginUrl() + "commands.html",
                },
                cache: {
                    cacheLocation: "sessionStorage",
                    storeAuthStateInCookie: false
                }
            };

            // Create an instance of MSAL
            const msalInstance = new msal.PublicClientApplication(msalConfig);
            try {
                let loginResponse;
                try {
                    // Call loginPopup directly without async delay to keep synchronous user gesture for Safari popup blocker
                    loginResponse = await msalInstance.loginPopup({
                        scopes: ["User.Read"] // Request user profile info
                    });
                } catch (error) {
                    console.error("Login failed:", error);
                    if (error.name === "BrowserAuthError" && error.errorCode === "interaction_in_progress") {
                        console.warn("MSAL interaction in progress, clearing session storage and retrying...");
                        for (let i = 0; i < sessionStorage.length; i++) {
                            const key = sessionStorage.key(i);
                            if (key && key.includes("interaction.status")) {
                                sessionStorage.removeItem(key);
                            }
                        }
                        // Retry login
                        loginResponse = await msalInstance.loginPopup({
                            scopes: ["User.Read"]
                        });
                    } else {
                        throw error;
                    }
                }

                if (loginResponse === null) {
                    console.error("Login failed:", loginResponse);
                } else {
                    console.log("Login successful:", loginResponse);
                    const tokenExchangeUrl = `${getServerUrl()}api/v2/addin/token/microsoft/`;
                    $http.post(tokenExchangeUrl, { access_token: loginResponse.accessToken })
                        .then(function (response) {
                            console.log("DRF token response:", response.data);
                            const token = response.data.token || response.data.key || response.data.access_token || response.data.accessToken || response.data.drawp_serverToken;
                            if (token) {
                                localStorage.setItem("drawp-serverToken", token);
                                document.getElementById('serverToken').innerHTML = token;
                            } else {
                                console.error("No token found in response", response.data);
                                localStorage.setItem("drawp-serverToken", loginResponse.accessToken);
                                document.getElementById('serverToken').innerHTML = loginResponse.accessToken;
                            }
                            localStorage.setItem("drawp-userEmail", loginResponse.account.username);
                            localStorage.setItem("drawp-login-type", "microsoft");
                            document.getElementById('userEmail').innerHTML = loginResponse.account.username;
                        }, function (error) {
                            console.error("Error exchanging microsoft token:", error);
                            // Fallback to raw access token so it doesn't break completely
                            localStorage.setItem("drawp-serverToken", loginResponse.accessToken);
                            localStorage.setItem("drawp-userEmail", loginResponse.account.username);
                            localStorage.setItem("drawp-login-type", "microsoft");
                            document.getElementById('userEmail').innerHTML = loginResponse.account.username;
                            document.getElementById('serverToken').innerHTML = loginResponse.accessToken;
                        });
                }
            } catch (error) {
                console.error("Login failed:", error);
                openLocalPowerpointLoginDialog();
            } finally {
                isLoginInProgress = false;
            }
        } else {
            openLocalPowerpointLoginDialog();
        }
    }

    const getGraphicOrganiser = (serverToken) => {
        const getGraphicOrganiserPromise = new Promise((resolve, reject) => {
            const url = `${getServerUrl()}google-addon/graphic-organizer/`;

            const myHeaders = getAuthorizationHeader(serverToken);

            const requestData = {
                email: getActiveUserEmail(),
                domain: 'microsoft'
            };

            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return getGraphicOrganiserPromise;
    }

    const getLvl3Filters = (serverToken) => {
        const getLvl3FiltersPromise = new Promise((resolve, reject) => {
            const url = `${getServerUrl()}google-addon/bundle/filters/`;

            const myHeaders = getAuthorizationHeader(serverToken);

            const requestData = {
                email: getActiveUserEmail(),
                domain: 'microsoft'
            };

            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return getLvl3FiltersPromise;
    };

    const getBundleLessonPlan = (bundleId, searchTags1, searchTags2, activeLanguage, serverToken) => {
        const getBundleLessonPlanPromise = new Promise((resolve, reject) => {
            const url = `${getServerUrl()}google-addon/bundle/lesson-plan/`;

            const myHeaders = getAuthorizationHeader(serverToken);

            const requestDataOne = {
                email: getActiveUserEmail(),
                bundle_id: bundleId,
                search_tags: searchTags1,
                active_language: activeLanguage,
                domain: 'microsoft'
            };

            const requestDataTwo = {
                email: getActiveUserEmail(),
                bundle_id: bundleId,
                search_tags: searchTags2,
                active_language: activeLanguage,
                domain: 'microsoft'
            };

            const responses = [];

            $http.post(url, requestDataOne, { headers: myHeaders }).then(function (response1) {
                responses.push(response1.data);
                return $http.post(url, requestDataTwo, { headers: myHeaders });
            }).then(function (response2) {
                responses.push(response2.data);
                resolve(responses);
            }).catch(function (error) {
                reject(error);
            });
        });

        return getBundleLessonPlanPromise;
    };

    const lvl3Search = (ellSearch, californiaElaEldFramework, activeLanguage, serverToken) => {
        const lvl3SearchPromise = new Promise((resolve, reject) => {
            const url = `${getServerUrl()}google-addon/bundle/search/`;

            const myHeaders = getAuthorizationHeader(serverToken);

            const requestData = {
                email: getActiveUserEmail(),
                ell_search: ellSearch,
                california_ela_eld_framework: californiaElaEldFramework,
                active_language: activeLanguage,
                domain: 'microsoft'
            };

            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return lvl3SearchPromise;
    };

    const getStripeBundlePrices = (bundleId, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/subscription/stripe-product-prices/`;

        const myHeaders = getAuthorizationHeader(serverToken);

        const requestData = {
            email: getActiveUserEmail(),
            bundle_id: bundleId,
            domain: 'microsoft'
        };

        // make api calls here, should return a promise
        const stripeBundlePricesPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return stripeBundlePricesPromise;
    };

    const getActiveSubscriptions = () => {
        const url = `${getServerUrl()}api/v2/google-addon/subscription/active/`;

        const myHeaders = getAuthorizationHeader(serverToken);

        const requestData = {
            email: getActiveUserEmail(),
            domain: 'microsoft'
        };

        const getActiveSubscriptionsPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return getActiveSubscriptionsPromise;
    };

    const getValidateCoupon = (coupon, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/validate-coupon/`;

        const myHeaders = getAuthorizationHeader(serverToken);

        const requestData = {
            coupon: coupon,
            domain: 'microsoft'
        };

        const getValidateCouponPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return getValidateCouponPromise;
    };

    const postPaymentToken = (token, priceIds, coupon, name, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/subscription-submit-token/`;

        const myHeaders = {
            'Content-Type': 'application/x-www-form-urlencoded'
        };

        const requestData = {
            stripeToken: token.id,
            email: getActiveUserEmail(),
            priceIds: JSON.stringify(priceIds),
            coupon: coupon,
            name: name,
            domain: 'microsoft'
        };

        let formEncodedData = $.param(requestData);

        const postPaymentTokenPromise = new Promise((resolve, reject) => {
            $http.post(url, formEncodedData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return postPaymentTokenPromise;
    };

    const renewSubscriptionToggle = (renew, bundleId, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/subscription-auto-renew-toggle/`;

        const myHeaders = getAuthorizationHeader(serverToken);

        const requestData = {
            email: getActiveUserEmail(),
            renew: renew,
            bundle_id: bundleId
        };

        const renewSubscriptionTogglePromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return renewSubscriptionTogglePromise;
    }

    const isBrowserPowerpoint = async () => {
        if (Office.context.requirements.isSetSupported("PowerPointApi", "1.1")) {
            return true;
        } else {
            return false;
        }
    };

    const goToPaymentScreen = (tool, language, data1, data2, $state) => {
        if (!DrawpService.isLoggedIn()) {
            $scope.launchSnackBar('Please login to purchase this tool.', 'Ok', true);
        } else {
            // if (isBrowserPowerpoint()) {
            //     initPaymentForBrowser(tool, language, data1, data2);
            // } else {
            if (data1 === undefined || data1 === null) {
                $state.go('payment', {
                    tool: tool,
                    language: language
                });
            } else {
                $state.go('payment', {
                    tool: tool,
                    language: language,
                    data1: data1,
                    data2: data2
                });
            }
            // }
        }
    };

    const initPaymentForBrowser = async (tool, language, data1, data2) => {

        const authUrl = "https://localhost:3000/payments.html?activeUserEmail=" + getActiveUserEmail() + "&server=" + process.env.NODE_ENV + "&tool=" + tool + "&language=" + language + "&data1=" + data1 + "&data2=" + data2;
        console.log(authUrl);
        Office.context.ui.displayDialogAsync(
            authUrl,
            { height: 70, width: 35, displayInIframe: false },
            function (result) {
                if (result.status === Office.AsyncResultStatus.Failed) {
                    console.error("Dialog failed to open:", result.error);
                    return;
                }

                if (result.status == 'succeeded') {
                    let authDialog = result.value;

                    // Add event handler for messages from dialog
                    authDialog.addEventHandler(Office.EventType.DialogMessageReceived, function (arg) {
                        console.log("DialogMessageReceived", arg);
                        const messageData = JSON.parse(arg.message);
                        // console.log(messageData.account.username);
                        authDialog.close();
                    });

                    // Add event handler for dialog closing
                    authDialog.addEventHandler(Office.EventType.DialogEventReceived, function (arg) {
                        console.log("DialogEventReceived", arg);
                    });
                }
            }
        );
    };

    const processPaymentMethod = (priceIds, coupon, name, payment_method_id, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/process-payment-method/`;

        const myHeaders = getAuthorizationHeader(serverToken);

        const requestData = {
            email: getActiveUserEmail(),
            priceIds: priceIds,
            coupon: coupon,
            name: name,
            payment_method_id
        };

        const processPaymentMethodPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return processPaymentMethodPromise;
    };

    const isLoggedIn = () => {
        const userEmail = document.getElementById('userEmail').innerHTML;
        if (userEmail !== '' && userEmail !== 'undefined' && userEmail !== 'null') {
            return true;
        } else if (localStorage.getItem('drawp-userEmail') !== null) {
            return true;
        }

        return false;
    };

    const logOut = () => {
        localStorage.removeItem('drawp-userEmail');
        document.getElementById('userEmail').innerHTML = '';
        document.getElementById('serverToken').innerHTML = '';
    };

    function sendAnalyticsHit(eventData, serverToken) {
        const url = `${getServerUrl()}google-addon/analytics/`;

        const myHeaders = getAuthorizationHeader(serverToken);

        eventData.forEach(event => {
            event.params = event.params || {};
            event.params['drawp-platform'] = 'microsoft-powerpoint-addin';
        });

        const requestData = {
            client_id: getActiveUserEmail(),
            user_id: getActiveUserEmail(),
            platform_type: "powerpoint",
            events: eventData,
        };

        const logMixpanelEventPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });

        return logMixpanelEventPromise;
    };

    function getPageName(tool) {
        const tool1 = parseInt(tool, 10);
        switch (tool1) {
            case 0:
                return 'graphic-organizers';

            case 3:
                return 'language-scaffolding-tool-ca';

            case 4:
                return 'language-scaffolding-tool-tx';

            case 6:
                return 'language-scaffolding-tool-6';

            case 7:
                return 'lesson-plan-detail';

            case 8:
                return 'lesson-plan-list';

            case 10:
                return 'vp-academic-journals';

            case 11:
                return 'vp-academic-journals';

            case 15:
                return 'translate';

            default:
                return 'N/A';
        }
    }

    const getLvl4Content = (searchTags, activeLanguage, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/school/framework/content/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            search_tags: searchTags,
            active_language: activeLanguage,
            framework_id: '5d4bd15a50c2d10f85311c6d',
            domain: 'microsoft'
        };
        const getLvl4ContentDataPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return getLvl4ContentDataPromise;
    };

    const getLvl4login = (serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/school/framework/filters/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            framework_id: '5d4bd15a50c2d10f85311c6d',
            domain: 'microsoft'
        };
        const getLvl4LoginPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return getLvl4LoginPromise;
    };

    const lvl4Search = (ellSearch, californiaElaEldFramework, activeLanguage) => {
        const url = `${getServerUrl()}api/v2/google-addon/school/framework/search/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            search: ellSearch,
            california_ela_eld_framework: californiaElaEldFramework,
            active_language: activeLanguage,
            framework_id: '5d4bd15a50c2d10f85311c6d',
            domain: 'microsoft'
        };
        const lvl4SearchPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return lvl4SearchPromise;
    };

    const setAddonDefaults = (language, audio, placeholder, lessonPlanSkipSlides, serverToken) => {
        const url = `${getServerUrl()}/google-addon/update-defaults/`;
        const myHeaders = getAuthenticatedUser(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            language,
            audio,
            placeholder,
        };

        if (lessonPlanSkipSlides != null) {
            requestData.lesson_plan_skip_slides = lessonPlanSkipSlides;
        }
        const setAddonDefaultsPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return setAddonDefaultsPromise;
    };

    const getLvl6login = (serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/school/framework/filters/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            framework_id: '600045776180724bcf6c3bf7',
            domain: 'microsoft'
        };
        const getLvl4LoginPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return getLvl4LoginPromise;
    };

    const getLvl6Content = (searchTags, activeLanguage, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/school/framework/content/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            search_tags: searchTags,
            active_language: activeLanguage,
            framework_id: '600045776180724bcf6c3bf7',
            domain: 'microsoft'
        };
        const getLvl6ContentPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return getLvl6ContentPromise;
    };

    const lvl6Search = (ellSearch, californiaElaEldFramework, activeLanguage) => {
        const url = `${getServerUrl()}api/v2/google-addon/school/framework/search/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            search: ellSearch,
            california_ela_eld_framework: californiaElaEldFramework,
            active_language: activeLanguage,
            framework_id: '600045776180724bcf6c3bf7',
            domain: 'microsoft'
        };
        const lvl6SearchPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return lvl6SearchPromise;
    };

    const getSubscriptionData = (packageId, serverToken) => {
        const url = `${getServerUrl()}api/v2/google-addon/subscription-data/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            package_id: packageId,
            domain: 'microsoft'
        };
        const getSubscriptionDataPromise = new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
        return getSubscriptionDataPromise;
    };

    const toolUsageCount = (packageId, count, serverToken) => {
        const url = `${getServerUrl()}api/v2/addon/tool-usage/`;
        const myHeaders = getAuthorizationHeader(serverToken);
        const requestData = {
            email: getActiveUserEmail(),
            package_id: packageId,
            count: count
        };
        return new Promise((resolve, reject) => {
            $http.post(url, requestData, { headers: myHeaders })
                .then(function (response) {
                    resolve(response);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    };

    const gAPageview = (pageName, nameVariant, lpLevel, subscription, username, serverToken) => {
        const events = [{
            name: 'page_view',
            params: {
                source: 'add-in',
                pageName,
                username,
                userLocale: "en-us",
                name_variant: nameVariant,
            },
        },];

        if (lpLevel != null) {
            events[0].params.lp_level = lpLevel;
        }

        if (subscription != null) {
            events[0].params.subscription = subscription;
        }

        sendAnalyticsHit(events, serverToken);
    };

    const gAStuPageview = (pageName, nameVariant, lpLevel, subscription, username, serverToken) => {
        const events = [{
            name: 'stu_page_view',
            params: {
                source: 'add-in',
                pageName,
                username,
                userLocale: "en",
                name_variant: nameVariant,
            },
        },];

        if (lpLevel != null) {
            events[0].params.lp_level = lpLevel;
        }

        if (subscription != null) {
            events[0].params.subscription = subscription;
        }

        sendAnalyticsHit(events, serverToken);
    };

    const gATextBoxAddEvent = (
        type,
        textBoxId,
        textBoxData,
        language,
        dok,
        langProf,
        areaOfFocus,
        audio,
        slideId,
        initialLanguage,
        initialAudio,
        addedToSlide,
        lpLevel,
        subscription,
        serverToken
    ) => {
        let pageName = 'language-scaffolding-tool-ca';
        if (lpLevel === 4) {
            pageName = 'language-scaffolding-tool-tx';
        } else if (lpLevel === 6) {
            pageName = 'language-scaffolding-tool-6';
        }
        const events = [{
            name: type,
            params: {
                source: 'add-in',
                pageName,
                textBoxId,
                textBoxData,
                language,
                dok,
                langProf,
                areaOfFocus,
                audio,
                username: getActiveUserEmail(),
                userLocale: "en",
                slideId,
                initialLanguage,
                initialAudio,
                addedToSlide,
                lp_level: lpLevel,
                subscription,
                pairs: false,
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gAStuTextBoxAddEvent = (
        type,
        textBoxId,
        textBoxData,
        language,
        dok,
        langProf,
        areaOfFocus,
        audio,
        slideId,
        initialLanguage,
        initialAudio,
        addedToSlide,
        lpLevel,
        subscription,
        serverToken
    ) => {
        let pageName = 'language-scaffolding-tool-ca';
        if (lpLevel === 4) {
            pageName = 'language-scaffolding-tool-tx';
        } else if (lpLevel === 6) {
            pageName = 'language-scaffolding-tool-6';
        }
        const events = [{
            name: `stu_${type}`,
            params: {
                source: 'add-in',
                pageName,
                textBoxId,
                textBoxData,
                language,
                dok,
                langProf,
                areaOfFocus,
                audio,
                username: getActiveUserEmail(),
                userLocale: "en",
                slideId,
                initialLanguage,
                initialAudio,
                addedToSlide,
                lp_level: lpLevel,
                subscription,
                pairs: false,
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gAGraphicOrganizerAddEvent = (
        type,
        id,
        url,
        language,
        theme,
        placeholder,
        slideId,
        initialLanguage,
        initialPlaceholder,
        filterTheme,
        subscription,
        premiumSlide,
        serverToken
    ) => {
        const events = [{
            name: type,
            params: {
                source: 'add-in',
                pageName: 'graphic-organizers',
                id,
                url,
                language,
                theme,
                placeholder,
                username: getActiveUserEmail(),
                slideId,
                initialLanguage,
                initialPlaceholder,
                filterTheme,
                subscription,
                premiumSlide,
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gALangScaffSearchEvent = (searchText, language, initialLanguage, lpLevel, subscription, serverToken) => {
        let pageName = 'language-scaffolding-tool-ca';
        if (lpLevel === 4) {
            pageName = 'language-scaffolding-tool-tx';
        } else if (lpLevel === 6) {
            pageName = 'language-scaffolding-tool-6';
        }
        const events = [{
            name: 'search_event',
            params: {
                source: 'add-in',
                pageName,
                searchText,
                language,
                initialLanguage,
                username: getActiveUserEmail(),
                userLocale: "en",
                subscription,
                lp_level: lpLevel,
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gAStuLangScaffSearchEvent = (searchText, language, initialLanguage, lpLevel, subscription, serverToken) => {
        let pageName = 'language-scaffolding-tool-ca';
        if (lpLevel === 4) {
            pageName = 'language-scaffolding-tool-tx';
        } else if (lpLevel === 6) {
            pageName = 'language-scaffolding-tool-6';
        }
        const events = [{
            name: 'stu_search_event',
            params: {
                source: 'add-in',
                pageName,
                searchText,
                language,
                initialLanguage,
                username: getActiveUserEmail(),
                userLocale: "en",
                subscription,
                lp_level: lpLevel,
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gAChangeSelectPairsEvent = (selectPairs, lpLevel, subscription, serverToken) => {
        let pageName = 'language-scaffolding-tool-ca';
        if (lpLevel === 4) {
            pageName = 'language-scaffolding-tool-tx';
        } else if (lpLevel === 6) {
            pageName = 'language-scaffolding-tool-6';
        }
        const events = [{
            name: 'select_pair_change',
            params: {
                source: 'add-in',
                pageName,
                selectPairs,
                username: getActiveUserEmail(),
                userLocale: "en",
                subscription,
                lp_level: lpLevel,
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gATextBoxPairAddEvent = (
        questionId,
        questionText,
        textBoxId,
        textBoxData,
        language,
        dok,
        langProf,
        areaOfFocus,
        audio,
        slideId,
        initialLanguage,
        initialAudio,
        addedToSlide,
        lpLevel,
        subscription,
        serverToken
    ) => {
        let pageName = 'language-scaffolding-tool-ca';
        if (lpLevel === 4) {
            pageName = 'language-scaffolding-tool-tx';
        } else if (lpLevel === 6) {
            pageName = 'language-scaffolding-tool-6';
        }
        const events = [{
            name: 'frame_question',
            params: {
                source: 'add-in',
                pageName,
                textBoxId: questionId,
                textBoxData: questionText,
                language,
                dok,
                langProf,
                areaOfFocus,
                audio,
                username: getActiveUserEmail(),
                userLocale: "en",
                slideId,
                initialLanguage,
                initialAudio,
                addedToSlide,
                lp_level: lpLevel,
                subscription,
                pairs: true,
            },
        },];

        textBoxData.forEach((element, key) => {
            events.push({
                name: 'frame_sentence',
                params: {
                    source: 'add-in',
                    pageName,
                    textBoxId: textBoxId[key],
                    textBoxData: element,
                    language,
                    dok,
                    langProf,
                    areaOfFocus,
                    audio,
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    slideId,
                    initialLanguage,
                    initialAudio,
                    addedToSlide,
                    lp_level: lpLevel,
                    subscription,
                    pairs: true,
                },
            });
        });

        sendAnalyticsHit(events, serverToken);
    };

    const gAPurchaseInitiatedEvent = (tool, language, data1, data2, serverToken) => {
        let events = null;
        const toolNumber = Number(tool);
        if (toolNumber === 0) {
            events = [{
                name: 'purchase_initiated',
                params: {
                    source: 'add-in',
                    pageName: 'graphic-organizers',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    theme: data1,
                    url: data2,
                },
            },];
        } else if (toolNumber === 7) {
            events = [{
                name: 'purchase_initiated',
                params: {
                    source: 'add-in',
                    pageName: 'lesson-plan-detail',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lesson_id: data1,
                    lesson_title: data2,
                },
            },];
        } else if (toolNumber === 8) {
            events = [{
                name: 'purchase_initiated',
                params: {
                    source: 'add-in',
                    pageName: 'lesson-plan-list',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lesson_id: data1,
                    lesson_title: data2,
                },
            },];
        } else if (tool === 10 || tool === 11) {
            events = [{
                name: 'purchase_initiated',
                params: {
                    source: 'add-in',
                    pageName: 'vp-academic-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    book_id: data1,
                    book_title: data2,
                },
            },];
        } else if (tool === 12 || tool === 13) {
            events = [{
                name: 'purchase_initiated',
                params: {
                    source: 'add-in',
                    pageName: 'vp-math-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    book_id: data1,
                    book_title: data2,
                },
            },];
        } else {
            let pageName = 'language-scaffolding-tool-6';
            if (toolNumber === 3) {
                pageName = 'language-scaffolding-tool-ca';
            } else if (toolNumber === 4) {
                pageName = 'language-scaffolding-tool-tx';
            }

            events = [{
                name: 'purchase_initiated',
                params: {
                    source: 'add-in',
                    pageName,
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lp_level: toolNumber,
                },
            },];
        }

        sendAnalyticsHit(events, serverToken);
    };

    const gAPurchaseCompletedEvent = (tool, language, data1, data2, serverToken) => {
        let events = null;
        const tool1 = parseInt(tool, 10);
        if (tool1 === 0) {
            events = [{
                name: 'purchase_completed',
                params: {
                    source: 'add-in',
                    pageName: 'graphic-organizers',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    theme: data1,
                    url: data2,
                },
            },];
        } else if (tool1 === 7) {
            events = [{
                name: 'purchase_completed',
                params: {
                    source: 'add-in',
                    pageName: 'lesson-plan-detail',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lesson_id: data1,
                    lesson_title: data2,
                },
            },];
        } else if (tool1 === 8) {
            events = [{
                name: 'purchase_completed',
                params: {
                    source: 'add-in',
                    pageName: 'lesson-plan-list',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lesson_id: data1,
                    lesson_title: data2,
                },
            },];
        } else if (tool === 10 || tool === 11) {
            events = [{
                name: 'purchase_failed',
                params: {
                    source: 'add-in',
                    pageName: 'vp-academic-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                },
            },];
        } else if (tool === 12 || tool === 13) {
            events = [{
                name: 'purchase_failed',
                params: {
                    source: 'add-in',
                    pageName: 'vp-math-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                },
            },];
        } else {
            let pageName = 'language-scaffolding-tool-6';
            if (tool1 === 3) {
                pageName = 'language-scaffolding-tool-ca';
            } else if (tool1 === 4) {
                pageName = 'language-scaffolding-tool-tx';
            }

            events = [{
                name: 'purchase_completed',
                params: {
                    source: 'add-in',
                    pageName,
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lp_level: tool1,
                },
            },];
        }

        sendAnalyticsHit(events, serverToken);
    };

    const gAPurchaseFailedEvent = (tool, language, data1, data2, serverToken) => {
        let events = null;
        const tool1 = parseInt(tool, 10);
        const pageName = getPageName(tool);

        if (tool1 === 0) {
            events = [{
                name: 'purchase_failed',
                params: {
                    source: 'add-in',
                    pageName: 'graphic-organizers',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    theme: data1,
                    url: data2,
                },
            },];
        } else if (tool1 === 7 || tool1 === 8) {
            events = [{
                name: 'purchase_failed',
                params: {
                    source: 'add-in',
                    pageName,
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                },
            },];
        } else if (tool === 10 || tool === 11) {
            events = [{
                name: 'purchase_failed',
                params: {
                    source: 'add-in',
                    pageName: 'vp-academic-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                },
            },];
        } else if (tool === 12 || tool === 13) {
            events = [{
                name: 'purchase_failed',
                params: {
                    source: 'add-in',
                    pageName: 'vp-math-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                },
            },];
        } else {
            events = [{
                name: 'purchase_failed',
                params: {
                    source: 'add-in',
                    pageName,
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lp_level: tool1,
                },
            },];
        }

        sendAnalyticsHit(events, serverToken);
    };

    const gAGeneralPurchaseEvents = (event, tool, serverToken) => {
        let events = null;
        const pageName = getPageName(tool);

        events = [{
            name: event,
            params: {
                source: 'add-in',
                pageName,
                username: getActiveUserEmail(),
                userLocale: "en",
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gAChangeSubsTypeEvent = (event, tool, data1, data2, serverToken) => {
        let events = null;
        events = [{
            name: event,
            params: {
                source: 'add-in',
                pageName: getPageName(tool),
                username: getActiveUserEmail(),
                userLocale: "en",
                old_selection: data1,
                new_selection: data2,
            },
        },];

        sendAnalyticsHit(events, serverToken);
    };

    const gAPurchaseButtonSelectedEvent = (tool, language, data1, data2, serverToken) => {
        let events = null;

        if (tool === 0) {
            events = [{
                name: 'purchase_button_selected',
                params: {
                    source: 'add-in',
                    pageName: 'graphic-organizers',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    theme: data1,
                    url: data2,
                },
            },];
        } else if (tool === 7) {
            events = [{
                name: 'purchase_button_selected',
                params: {
                    source: 'add-in',
                    pageName: 'lesson-plan-detail',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lesson_id: data1,
                    lesson_title: data2,
                },
            },];
        } else if (tool === 8) {
            events = [{
                name: 'purchase_button_selected',
                params: {
                    source: 'add-in',
                    pageName: 'lesson-plan-list',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lesson_id: data1,
                    lesson_title: data2,
                },
            },];
        } else if (tool === 10 || tool === 11) {
            events = [{
                name: 'purchase_button_selected',
                params: {
                    source: 'add-in',
                    pageName: 'vp-academic-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    book_id: data1,
                    book_title: data2,
                },
            },];
        } else if (tool === 12 || tool === 13) {
            events = [{
                name: 'purchase_button_selected',
                params: {
                    source: 'add-in',
                    pageName: 'vp-math-journals',
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    book_id: data1,
                    book_title: data2,
                },
            },];
        } else if (tool === 14 || tool === 15) {
            events = [{
                name: 'purchase_button_selected',
                params: {
                    source: 'add-in',
                    pageName: getPageName(tool),
                    username: getActiveUserEmail(),
                    userLocale: "en",
                },
            },];
        } else {
            let pageName = 'language-scaffolding-tool-6';
            if (tool === 3) {
                pageName = 'language-scaffolding-tool-ca';
            } else if (tool === 4) {
                pageName = 'language-scaffolding-tool-tx';
            }

            events = [{
                name: 'purchase_button_selected',
                params: {
                    source: 'add-in',
                    pageName,
                    username: getActiveUserEmail(),
                    userLocale: "en",
                    language,
                    lp_level: tool,
                },
            },];
        }

        sendAnalyticsHit(events, serverToken);
    };

    /**
     *
     * @param {Will be passed as event name} name
     * @param {*} lessonId
     * @param {*} title
     */
    const gALessonPlansEvent = (name, lessonId, title, subscription, serverToken) => {
        const events = [{
            name,
            params: {
                source: 'add-in',
                pageName: 'lesson-plans-tool',
                username: getActiveUserEmail(),
                userLocale: "en",
                title,
                lesson_id: lessonId,
            },
        },];

        if (subscription != null) {
            events[0].params.subscription = subscription;
        }

        sendAnalyticsHit(events, serverToken);
    };

    const captureException = (error, contextMessage, extraData) => {
        if (contextMessage) {
            console.error(`[Drawp] ${contextMessage}:`, error);
        } else {
            console.error('[Drawp Error]:', error);
        }
        if (window.Sentry && typeof window.Sentry.captureException === 'function') {
            window.Sentry.withScope((scope) => {
                if (contextMessage) {
                    scope.setTag('context', contextMessage);
                }
                const activeEmail = getActiveUserEmail();
                if (activeEmail) {
                    scope.setUser({ email: activeEmail });
                }
                if (extraData) {
                    scope.setExtra('extraData', extraData);
                }
                const errObj = (error instanceof Error) ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));
                window.Sentry.captureException(errObj);
            });
        }
    };

    const gAErrorEvent = (toolName, username, errorMessage, serverToken) => {
        const events = [{
            name: 'error_event',
            params: {
                toolName,
                username,
                errorMessage
            },
        },];

        sendAnalyticsHit(events, serverToken);

        if (window.Sentry) {
            window.Sentry.withScope((scope) => {
                if (toolName) scope.setTag('toolName', toolName);
                if (username) scope.setUser({ username });
                window.Sentry.captureMessage(errorMessage, 'error');
            });
        }
    };

    const gAStuErrorEvent = (toolName, username, errorMessage, serverToken) => {
        const events = [{
            name: 'stu_error_event',
            params: {
                toolName,
                username,
                errorMessage
            },
        },];

        sendAnalyticsHit(events, serverToken);

        if (window.Sentry) {
            window.Sentry.withScope((scope) => {
                if (toolName) scope.setTag('toolName', toolName);
                if (username) scope.setUser({ username });
                window.Sentry.captureMessage(`[Student] ${errorMessage}`, 'error');
            });
        }
    };

    const gABasicEvent = (
        type,
        params,
        serverToken
    ) => {
        const events = [{
            name: type,
            params
        },];

        sendAnalyticsHit(events, serverToken);
    };

    function concatAndConvertToBase64(slices) {
        let totalLength = 0;
        for (let i = 0; i < slices.length; i++) {
            totalLength += slices[i].length;
        }
        let fullArray = new Uint8Array(totalLength);
        let offset = 0;
        for (let i = 0; i < slices.length; i++) {
            fullArray.set(slices[i], offset);
            offset += slices[i].length;
        }
        slices.length = 0;

        let binary = "";
        const chunkSize = 8192;
        for (let i = 0; i < fullArray.length; i += chunkSize) {
            const chunk = fullArray.subarray(i, i + chunkSize);
            binary += String.fromCharCode.apply(null, chunk);
        }
        fullArray = null;

        const resultBase64 = window.btoa(binary);
        binary = null;
        return resultBase64;
    }

    let activeOfficeFile = null;

    const closeActiveOfficeFile = () => {
        if (activeOfficeFile) {
            try {
                activeOfficeFile.closeAsync();
            } catch (e) {
                console.warn("Error closing active file handle:", e);
            }
            activeOfficeFile = null;
        }
    };

    /**
     * Exports the entire PowerPoint presentation (.pptx) as a Base64 string.
     * 
     * PERFORMANCE & MEMORY NOTE:
     * - Office.js getFileAsync() retrieves the ENTIRE presentation file (including all slides,
     *   embedded images, videos, and master layouts), not just the selected slide.
     * - Converting the compressed binary file to Base64 ASCII inflates payload size in RAM by ~33.3%.
     * - For large decks (e.g. 50MB+), this results in significant JS heap memory allocation and garbage collection pauses.
     */
    const getPresentationBase64 = () => {
        return new Promise((resolve, reject) => {
            console.time("Base64 Presentation Export");
            closeActiveOfficeFile();

            const attemptGetFile = (requestedSliceSize) => {
                Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: requestedSliceSize }, (result) => {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        if (requestedSliceSize > 65536) {
                            console.warn("Requested sliceSize failed, falling back to 64KB sliceSize:", result.error.message);
                            attemptGetFile(65536);
                            return;
                        }
                        console.timeEnd("Base64 Presentation Export");
                        reject(new Error(result.error.message));
                        return;
                    }
                    const file = result.value;
                    activeOfficeFile = file;
                    const sliceCount = file.sliceCount;
                    let slicesReceived = 0;
                    let fileData = [];

                    const getSlice = (sliceIndex) => {
                        file.getSliceAsync(sliceIndex, (sliceResult) => {
                            if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                                file.closeAsync();
                                activeOfficeFile = null;
                                console.timeEnd("Base64 Presentation Export");
                                reject(new Error(sliceResult.error.message));
                                return;
                            }
                            fileData.push(sliceResult.value.data);
                            slicesReceived++;
                            if (slicesReceived === sliceCount) {
                                file.closeAsync();
                                activeOfficeFile = null;
                                try {
                                    const base64 = concatAndConvertToBase64(fileData);
                                    console.timeEnd("Base64 Presentation Export");
                                    resolve(base64);
                                } catch (e) {
                                    console.timeEnd("Base64 Presentation Export");
                                    reject(e);
                                }
                            } else {
                                getSlice(slicesReceived);
                            }
                        });
                    };
                    getSlice(0);
                });
            };

            // Office on the web (OfficeOnline) limits sliceSize to 64KB (65536 bytes). Desktop uses 4MB (4194304 bytes).
            const isOfficeOnline = Office.context && (Office.context.platform === Office.PlatformType.OfficeOnline || Office.context.platform === "OfficeOnline");
            const initialSliceSize = isOfficeOnline ? 65536 : 4194304;
            attemptGetFile(initialSliceSize);
        });
    };

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const translateText = (sourceText, targetLanguage, sourceLanguage = "all", maxRetries = 3, baseDelay = 1000) => {
        return new Promise((resolve, reject) => {
            const url = `${getServerUrl()}api/v2/google-addon/translate/`;
            const requestData = {
                source_text: sourceText,
                source_language: sourceLanguage || "all",
                translated_language: targetLanguage,
                email: getActiveUserEmail()
            };
            const myHeaders = getAuthorizationHeader(getAccessToken());

            const executeRequest = (attempt = 0) => {
                $http.post(url, requestData, { headers: myHeaders })
                    .then(function (response) {
                        if (response.data && response.data.translated_text) {
                            resolve(response.data.translated_text);
                        } else {
                            resolve(sourceText);
                        }
                    }, async function (error) {
                        if (error && error.status === 429 && attempt < maxRetries) {
                            let delay = Math.pow(2, attempt) * baseDelay + Math.floor(Math.random() * 500);
                            try {
                                const retryAfter = error.headers && typeof error.headers === "function" ? error.headers("retry-after") : null;
                                if (retryAfter) {
                                    const parsedSeconds = parseInt(retryAfter, 10);
                                    if (!isNaN(parsedSeconds) && parsedSeconds > 0) {
                                        delay = Math.max(delay, parsedSeconds * 1000);
                                    }
                                }
                            } catch (e) { }

                            console.warn(`[SlideTranslation] translate received 429 (Too Many Requests). Retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})...`);
                            await sleep(delay);
                            return executeRequest(attempt + 1);
                        }

                        console.error("Translation API error:", error);
                        resolve(sourceText);
                    });
            };

            executeRequest();
        });
    };

    const translateTextArray = (sourceTexts, targetLanguage, sourceLanguage = "all", isCancelledRef = null) => {
        return new Promise(async (resolve, reject) => {
            if (!sourceTexts || !Array.isArray(sourceTexts) || sourceTexts.length === 0) {
                return resolve([]);
            }
            const url = `${getServerUrl()}api/v2/google-addon/translate-array/`;
            const myHeaders = getAuthorizationHeader(getAccessToken());

            const isCancelled = () => {
                if (typeof isCancelledRef === "function") return isCancelledRef();
                if (isCancelledRef && isCancelledRef.current) return true;
                return false;
            };

            const BATCH_SIZES = [50, 30, 20, 10];
            const MAX_RETRIES = 3;

            // Reset batch size to 50 at the start of every slide translation call
            let currentBatchSize = BATCH_SIZES[0]; // first try = 50
            let attempt = 0;
            let currentIndex = 0;
            const results = [];

            // --- TEMPORARY TEST HOOK FOR 429 TESTING ---
            // Simulates 429 Too Many Requests for the first 2 attempts, then succeeds on 3rd attempt:
            // let test429Remaining = 2;

            console.log(`[SlideTranslation] Initializing slide text translation with batch size ${currentBatchSize} (${sourceTexts.length} texts)...`);

            while (currentIndex < sourceTexts.length) {
                if (isCancelled()) {
                    console.warn("[SlideTranslation] translateTextArray cancelled by user.");
                    break;
                }

                const chunk = sourceTexts.slice(currentIndex, currentIndex + currentBatchSize);
                const requestData = {
                    source_text: chunk,
                    source_language: sourceLanguage || "all",
                    translated_language: targetLanguage
                };

                try {
                    // Temporary test: simulate 429 Too Many Requests
                    // if (test429Remaining > 0) {
                    //     test429Remaining--;
                    //     console.warn(`[TEST HOOK] Simulating 429 Too Many Requests! (${test429Remaining} simulated failures remaining before real API call)`);
                    //     throw { status: 429, headers: () => null, data: { message: "Simulated 429 Too Many Requests" } };
                    // }

                    const response = await $http.post(url, requestData, { headers: myHeaders });
                    if (response.data && Array.isArray(response.data.translations)) {
                        const translatedList = response.data.translations.map((t, idx) => {
                            return (t && typeof t.text === "string") ? t.text : chunk[idx];
                        });
                        results.push(...translatedList);
                        currentIndex += chunk.length;
                        attempt = 0;
                    } else {
                        console.warn("[SlideTranslation] translate-array unexpected response format, falling back to original texts:", response.data);
                        results.push(...chunk);
                        currentIndex += chunk.length;
                        attempt = 0;
                    }
                } catch (error) {
                    if (isCancelled()) {
                        console.warn("[SlideTranslation] translateTextArray cancelled by user during error handling.");
                        break;
                    }

                    if (error && error.status === 429 && attempt < MAX_RETRIES) {
                        attempt++;
                        // Progressive batch size reduction: 50 -> 30 -> 20 -> 10
                        currentBatchSize = BATCH_SIZES[Math.min(attempt, BATCH_SIZES.length - 1)];

                        // Exponential backoff delay: attempt 1 -> 1s, attempt 2 -> 2s, attempt 3 -> 4s (+ jitter)
                        let delay = Math.pow(2, attempt - 1) * 1000 + Math.floor(Math.random() * 500);
                        try {
                            const retryAfter = error.headers && typeof error.headers === "function" ? error.headers("retry-after") : null;
                            if (retryAfter) {
                                const parsedSeconds = parseInt(retryAfter, 10);
                                if (!isNaN(parsedSeconds) && parsedSeconds > 0) {
                                    delay = Math.max(delay, parsedSeconds * 1000);
                                }
                            }
                        } catch (e) { }

                        console.warn(`[SlideTranslation] translate-array received 429 (Too Many Requests). Retrying in ${delay}ms with reduced batch size ${currentBatchSize} (attempt ${attempt}/${MAX_RETRIES})...`);
                        await sleep(delay);
                        // Do NOT advance currentIndex; retry with smaller batch size from currentIndex
                        continue;
                    }

                    if (error && error.status === 429) {
                        console.error(`[SlideTranslation] translate-array failed after ${MAX_RETRIES} retries with 429. Falling back to source texts for current batch.`);
                    } else {
                        console.error("Translation Array API error:", error);
                    }
                    results.push(...chunk);
                    currentIndex += chunk.length;
                    attempt = 0;
                }
            }

            resolve(results);
        });
    };

    function parseWordRanges(textRange, originalText) {
        const words = [];
        let start = 0;
        const len = originalText.length;

        while (start < len) {
            let spaceStart = start;
            while (start < len && (originalText[start] === " " || originalText[start] === "\t" || originalText[start] === "\r" || originalText[start] === "\n")) {
                start++;
            }
            if (start > spaceStart) {
                words.push({
                    isWord: false,
                    text: originalText.substring(spaceStart, start),
                    start: spaceStart,
                    length: start - spaceStart
                });
            }
            if (start >= len) break;

            let wordStart = start;
            while (start < len && originalText[start] !== " " && originalText[start] !== "\t" && originalText[start] !== "\r" && originalText[start] !== "\n") {
                start++;
            }
            if (start > wordStart) {
                const wordLen = start - wordStart;
                const r = textRange.getSubstring(wordStart, wordLen);
                r.load([
                    "font/name",
                    "font/size",
                    "font/color",
                    "font/bold",
                    "font/italic",
                    "font/underline"
                ]);
                words.push({
                    isWord: true,
                    text: originalText.substring(wordStart, start),
                    start: wordStart,
                    length: wordLen,
                    range: r
                });
            }
        }
        return words;
    }

    const replicateAndTranslateSlide = async (slideIndex, targetLanguage, preloadedBase64 = null, isCancelledRef = null) => {
        console.time("Slide Translation");
        const checkCancellation = () => {
            if (typeof isCancelledRef === "function" && isCancelledRef()) {
                throw new Error("Translation process cancelled by user.");
            }
        };

        try {
            return await PowerPoint.run(async (context) => {
                checkCancellation();
                const safeUntrack = (obj) => {
                    if (!obj) return;
                    try {
                        if (typeof obj.untrack === "function") {
                            obj.untrack();
                        } else if (context && context.trackedObjects && typeof context.trackedObjects.remove === "function") {
                            context.trackedObjects.remove(obj);
                        }
                    } catch (e) { }
                };

                const presentation = context.presentation;
                const slides = presentation.slides;
                slides.load("items");
                await context.sync();
                checkCancellation();

                if (slideIndex < 0 || slideIndex >= slides.items.length) {
                    throw new Error("Invalid slide index.");
                }

                const originalSlide = slides.items[slideIndex];
                context.load(originalSlide, "id");
                await context.sync();
                checkCancellation();
                const originalSlideId = originalSlide.id;

                let base64String = null;
                // Check if PowerPointApi 1.8 slide.exportAsBase64() is supported for single-slide Base64 extraction
                if (typeof originalSlide.exportAsBase64 === "function") {
                    console.log(`[SlideTranslation] Exporting single slide #${slideIndex + 1} via slide.exportAsBase64()...`);
                    const singleBase64Result = originalSlide.exportAsBase64();
                    await context.sync();
                    checkCancellation();
                    base64String = singleBase64Result.value;
                } else {
                    console.log(`[SlideTranslation] Falling back to deck-wide Base64 export for slide #${slideIndex + 1}...`);
                    base64String = preloadedBase64 || await getPresentationBase64();
                    checkCancellation();
                }

                console.log(`[SlideTranslation] Duplicating slide via insertSlidesFromBase64...`);
                console.time("Insert Slide From Base64");
                presentation.insertSlidesFromBase64(base64String, {
                    sourceSlideIds: [originalSlideId],
                    targetSlideId: originalSlideId,
                    formatting: PowerPoint.InsertSlideFormatting ? PowerPoint.InsertSlideFormatting.keepSourceFormatting : "KeepSourceFormatting"
                });
                await context.sync();
                checkCancellation();
                console.timeEnd("Insert Slide From Base64");
                console.log(`[SlideTranslation] Slide duplication successful.`);

                // Immediately release heavy Base64 payload from RAM memory
                base64String = null;

                slides.load("items");
                await context.sync();
                const newSlide = slides.items[slideIndex + 1];
                context.load(newSlide, "id");
                const shapes = newSlide.shapes;
                shapes.load("items");
                await context.sync();

                function parseParagraphInfo(textRange, text) {
                    const paragraphs = [];
                    if (!text || text.length === 0) return paragraphs;

                    const cleanText = text.replace(/\r\n/g, "\r").replace(/\n/g, "\r");
                    let start = 0;
                    const lines = cleanText.split("\r");

                    for (let i = 0; i < lines.length; i++) {
                        const lineText = lines[i];
                        const len = lineText.length;
                        if (len > 0) {
                            try {
                                const sub = textRange.getSubstring(start, len);
                                sub.load([
                                    "font/name",
                                    "font/size",
                                    "font/color",
                                    "font/bold",
                                    "font/italic",
                                    "font/underline",
                                    "paragraphFormat/horizontalAlignment",
                                    "paragraphFormat/indentLevel",
                                    "paragraphFormat/bulletFormat/type",
                                    "paragraphFormat/bulletFormat/style",
                                    "paragraphFormat/bulletFormat/visible"
                                ]);
                                paragraphs.push({
                                    start,
                                    length: len,
                                    text: lineText,
                                    range: sub
                                });
                            } catch (e) { }
                        }
                        start += len + 1;
                    }
                    return paragraphs;
                }

                function isTableShape(shapeOrType) {
                    if (!shapeOrType) return false;
                    const type = (typeof shapeOrType === "object" && shapeOrType.type) ? shapeOrType.type : shapeOrType;
                    if (!type) return false;
                    const t = String(type).toLowerCase();
                    return t.includes("table") || (PowerPoint.ShapeType && type === PowerPoint.ShapeType.table);
                }

                function isNonTextFrameShape(shapeOrType, shapeName = "") {
                    if (!shapeOrType) return true;
                    const type = (typeof shapeOrType === "object" && shapeOrType.type) ? shapeOrType.type : shapeOrType;
                    const name = (typeof shapeOrType === "object" && shapeOrType.name) ? shapeOrType.name : shapeName;
                    if (!type) return true;

                    const t = String(type).toLowerCase();
                    const n = String(name || "").toLowerCase();

                    // If shape name indicates picture/image/media placeholder or unsupported placeholder
                    if (
                        n.includes("picture") ||
                        n.includes("image") ||
                        n.includes("photo") ||
                        n.includes("media") ||
                        n.includes("video") ||
                        n.includes("audio") ||
                        n.includes("table") ||
                        n.includes("chart") ||
                        n.includes("diagram")
                    ) {
                        return true;
                    }

                    return (
                        t.includes("image") ||
                        t.includes("picture") ||
                        t.includes("media") ||
                        t.includes("graphic") ||
                        t.includes("line") ||
                        t.includes("table") ||
                        t.includes("group") ||
                        t.includes("connector") ||
                        t.includes("ink") ||
                        t.includes("smartart") ||
                        t.includes("diagram") ||
                        t.includes("chart") ||
                        t.includes("ole") ||
                        t.includes("video") ||
                        t.includes("audio") ||
                        t === "3dmodel" ||
                        t === "svg" ||
                        t === "icon" ||
                        t === "unsupported" ||
                        t === "unset"
                    );
                }

                function isNonTextShape(shapeOrType, shapeName = "") {
                    return isNonTextFrameShape(shapeOrType, shapeName) && !isTableShape(shapeOrType);
                }

                console.time("Phase 1: Batched Text & Format Gathering");

                // BATCH SYNC 1: Load shape types safely for ALL top-level elements and ungroup any Group shapes
                console.log(`[SlideTranslation] Slide ${slideIndex + 1}: Initial top-level shapes count = ${shapes.items.length}`);

                let hasGroups = true;
                let ungroupPass = 0;

                while (hasGroups && ungroupPass < 5) {
                    ungroupPass++;
                    let foundGroup = false;

                    shapes.load("items");
                    await context.sync();

                    for (let idx = 0; idx < shapes.items.length; idx++) {
                        const shape = shapes.items[idx];
                        try {
                            shape.load(["id", "name", "type"]);
                        } catch (e) {
                            shape.load("type");
                        }
                    }
                    await context.sync();

                    for (let idx = 0; idx < shapes.items.length; idx++) {
                        const shape = shapes.items[idx];
                        const typeStr = String(shape.type).toLowerCase();
                        const isGroup = typeStr.includes("group") || shape.type === PowerPoint.ShapeType.group;

                        if (isGroup) {
                            foundGroup = true;
                            console.log(`[SlideTranslation] Found Group Shape #${idx + 1} ("${shape.name || 'Group'}", ID: "${shape.id || 'N/A'}"). Calling shape.group.ungroup()...`);
                            try {
                                if (shape.group) {
                                    shape.group.ungroup();
                                }
                            } catch (e) {
                                console.warn("[SlideTranslation] Error calling ungroup():", e);
                            }
                        }
                    }

                    if (foundGroup) {
                        console.log(`[SlideTranslation] Syncing Ungroup operation (Pass #${ungroupPass})...`);
                        await context.sync();
                    } else {
                        hasGroups = false;
                    }
                }

                // Load final list of shapes after ungrouping
                shapes.load("items");
                await context.sync();

                const shapesToProcess = [];
                for (let idx = 0; idx < shapes.items.length; idx++) {
                    const shape = shapes.items[idx];
                    try {
                        shape.load(["id", "name", "type"]);
                    } catch (e) {
                        shape.load("type");
                    }
                    shapesToProcess.push(shape);
                }
                await context.sync();

                console.log(`[SlideTranslation] Total shapes to process after ungrouping = ${shapesToProcess.length}`);
                for (let idx = 0; idx < shapesToProcess.length; idx++) {
                    const shape = shapesToProcess[idx];
                    console.log(`[SlideTranslation] Shape #${idx + 1}/${shapesToProcess.length} -> Name: "${shape.name || 'N/A'}", ID: "${shape.id || 'N/A'}", Type: "${shape.type}"`);
                }

                // Load textFrame property only for shapes that support text frames (excluding tables, groups, lines, images, etc.)
                for (let idx = 0; idx < shapesToProcess.length; idx++) {
                    const shape = shapesToProcess[idx];
                    if (!isNonTextFrameShape(shape.type, shape.name)) {
                        try {
                            console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || shape.type}): Loading textFrame...`);
                            shape.load(["textFrame", "textFrame/hasText"]);
                        } catch (e) {
                            console.warn(`[SlideTranslation] Shape #${idx + 1} (${shape.name || shape.type}): Skipping textFrame load:`, e);
                        }
                    } else if (isTableShape(shape.type)) {
                        console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || 'Table'}): Table shape detected (will load via table API).`);
                    } else {
                        console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || shape.type}): Skipped non-textFrame shape (Type: ${shape.type})`);
                    }
                }
                console.log("[SlideTranslation] Syncing Batch 1b: Loading textFrames...");
                await context.sync();

                const shapeTableMap = new Map();
                const tableCellsMap = new Map();

                for (let idx = 0; idx < shapesToProcess.length; idx++) {
                    const shape = shapesToProcess[idx];
                    if (isTableShape(shape.type)) {
                        console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || 'Table'}): Loading table dimensions...`);
                        try {
                            if (typeof shape.getTable === "function") {
                                const table = shape.getTable();
                                table.load(["rowCount", "columnCount"]);
                                shapeTableMap.set(shape, table);
                            }
                        } catch (e) {
                            console.warn(`[SlideTranslation] Shape #${idx + 1}: Failed to getTable():`, e);
                        }
                    } else if (!isNonTextFrameShape(shape.type, shape.name) && shape.textFrame && shape.textFrame.hasText) {
                        console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || 'TextBox'}): Loading textRange properties...`);
                        const textRange = shape.textFrame.textRange;
                        textRange.load([
                            "text",
                            "start",
                            "font/name",
                            "font/size",
                            "font/color",
                            "font/bold",
                            "font/italic",
                            "font/underline",
                            "paragraphFormat/horizontalAlignment"
                        ]);
                        try {
                            textRange.hyperlinks.load("items");
                        } catch (e) { }
                        try {
                            textRange.paragraphs.load("items");
                        } catch (e) { }
                    }
                }
                // Queue slide-level hyperlinks so we can detect hyperlinks inside table cells and shapes
                try {
                    if (newSlide.hyperlinks) {
                        newSlide.hyperlinks.load(["items"]);
                    }
                } catch (e) { }
                console.log("[SlideTranslation] Syncing Batch 1c: Loading text ranges and table dimensions...");
                await context.sync();

                // BATCH SYNC 2: Queue table cell loads & hyperlink target ranges for all shapes
                const linkLoadItems = [];
                const shapeParagraphsMap = new Map();
                const slideLinkLoadEntries = [];

                try {
                    const slideHyperlinkItems = (newSlide.hyperlinks && newSlide.hyperlinks.items) ? newSlide.hyperlinks.items : [];
                    console.log(`[SlideTranslation] slide.hyperlinks detected ${slideHyperlinkItems.length} total links on slide.`);
                    for (const sLink of slideHyperlinkItems) {
                        try {
                            sLink.load(["address", "screenTip", "type"]);
                            let linkedRange = null;
                            if (typeof sLink.getLinkedTextRangeOrNullObject === "function") {
                                linkedRange = sLink.getLinkedTextRangeOrNullObject();
                                linkedRange.load(["start", "length", "text", "isNullObject"]);
                            }
                            let linkedShape = null;
                            if (typeof sLink.getLinkedShapeOrNullObject === "function") {
                                linkedShape = sLink.getLinkedShapeOrNullObject();
                                linkedShape.load(["id", "name", "type", "isNullObject"]);
                            } else if (typeof sLink.getParentShapeOrNullObject === "function") {
                                linkedShape = sLink.getParentShapeOrNullObject();
                                linkedShape.load(["id", "name", "type", "isNullObject"]);
                            }
                            slideLinkLoadEntries.push({ link: sLink, linkedRange, linkedShape });
                        } catch (e) {
                            console.warn("[SlideTranslation] Error queueing slide hyperlink item:", e);
                        }
                    }
                } catch (e) {
                    console.warn("[SlideTranslation] Error inspecting slide.hyperlinks:", e);
                }

                for (let idx = 0; idx < shapesToProcess.length; idx++) {
                    const shape = shapesToProcess[idx];
                    if (isTableShape(shape.type)) {
                        const table = shapeTableMap.get(shape);
                        if (table && typeof table.rowCount === "number" && typeof table.columnCount === "number") {
                            console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || 'Table'}): Queueing table cells (${table.rowCount}x${table.columnCount})...`);
                            const cells = [];
                            for (let r = 0; r < table.rowCount; r++) {
                                for (let c = 0; c < table.columnCount; c++) {
                                    try {
                                        const cell = table.getCellOrNullObject(r, c);
                                        cell.load([
                                            "isNullObject",
                                            "text"
                                        ]);
                                        try {
                                            if (cell.hyperlinks) {
                                                cell.hyperlinks.load(["items"]);
                                            }
                                        } catch (e) { }
                                        cells.push({ cell, table, shape, row: r, col: c });
                                    } catch (e) {
                                        console.warn(`[SlideTranslation] Failed to queue cell (${r}, ${c}):`, e);
                                    }
                                }
                            }
                            tableCellsMap.set(shape, cells);
                        } else {
                            console.warn(`[SlideTranslation] Shape #${idx + 1}: Table dimensions not loaded or invalid:`, table ? `rowCount=${table.rowCount}, columnCount=${table.columnCount}` : 'table is null');
                        }
                    } else if (!isNonTextFrameShape(shape.type, shape.name) && shape.textFrame && shape.textFrame.hasText) {
                        const textRange = shape.textFrame.textRange;
                        console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || 'TextBox'}): Text preview: "${(textRange.text || '').substring(0, 40).replace(/\n/g, ' ')}"`);
                        if (textRange.text && textRange.text.trim().length > 0) {
                            const paragraphInfos = parseParagraphInfo(textRange, textRange.text);
                            shapeParagraphsMap.set(textRange, paragraphInfos);

                            const hyperlinkItems = (textRange.hyperlinks && textRange.hyperlinks.items) ? textRange.hyperlinks.items : [];
                            for (const link of hyperlinkItems) {
                                link.load(["address", "screenTip"]);
                                try {
                                    const linkedRange = link.getLinkedTextRangeOrNullObject();
                                    linkedRange.load(["start", "length"]);
                                    linkLoadItems.push({ textRange, link, linkedRange });
                                } catch (e) {
                                    console.warn(`[SlideTranslation] Shape #${idx + 1}: Skipping invalid linkedRange for hyperlink:`, e);
                                }
                            }
                        }
                    }
                }
                console.log("[SlideTranslation] Syncing Batch 2: Executing table cell and hyperlink loads...");
                await context.sync();

                // BATCH SYNC 3: Queue word ranges for mixed shapes (table cells are already loaded in Batch 2)
                const mixedWordItems = [];

                for (let idx = 0; idx < shapesToProcess.length; idx++) {
                    const shape = shapesToProcess[idx];
                    if (isTableShape(shape.type)) {
                        // Table cells were already loaded in Batch 2 sync
                    } else if (!isNonTextFrameShape(shape.type, shape.name) && shape.textFrame && shape.textFrame.hasText) {
                        const textRange = shape.textFrame.textRange;
                        const originalText = textRange.text;
                        if (!originalText || originalText.trim().length === 0) continue;

                        const font = textRange.font;
                        const hasLinks = (textRange.hyperlinks && textRange.hyperlinks.items) ? textRange.hyperlinks.items.length > 0 : false;
                        let trimmedLength = originalText.length;
                        while (trimmedLength > 0 && (originalText[trimmedLength - 1] === "\r" || originalText[trimmedLength - 1] === "\n")) {
                            trimmedLength--;
                        }

                        const isUniform = !hasLinks && trimmedLength > 0 &&
                            (font.name !== null && font.name !== undefined) &&
                            (font.size !== null && font.size !== undefined) &&
                            (font.color !== null && font.color !== undefined) &&
                            (font.bold !== null && font.bold !== undefined) &&
                            (font.italic !== null && font.italic !== undefined) &&
                            (font.underline !== null && font.underline !== undefined);

                        if (!isUniform) {
                            console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || 'TextBox'}): Parsing mixed word ranges for "${originalText.substring(0, 30)}..."`);
                            const words = parseWordRanges(textRange, originalText);
                            mixedWordItems.push({
                                textRange,
                                originalText,
                                words
                            });
                        } else {
                            console.log(`[SlideTranslation] Shape #${idx + 1} (${shape.name || 'TextBox'}): Uniform formatting detected.`);
                        }
                    }
                }
                console.log("[SlideTranslation] Syncing Batch 3: Executing mixed word ranges and cell property loads...");
                await context.sync();

                // Build map of charHyperlinks for each textRange
                const shapeHyperlinksMap = new Map();
                for (const item of linkLoadItems) {
                    let charHyperlinks = shapeHyperlinksMap.get(item.textRange);
                    if (!charHyperlinks) {
                        charHyperlinks = new Array(item.textRange.text.length).fill(null);
                        shapeHyperlinksMap.set(item.textRange, charHyperlinks);
                    }
                    if (!item.linkedRange.isNullObject) {
                        const startRel = item.linkedRange.start - item.textRange.start;
                        const len = item.linkedRange.length;
                        for (let idx = startRel; idx < startRel + len; idx++) {
                            if (idx >= 0 && idx < charHyperlinks.length) {
                                charHyperlinks[idx] = {
                                    address: item.link.address,
                                    screenTip: item.link.screenTip
                                };
                            }
                        }
                    }
                    // Release hyperlink proxy objects from memory
                    safeUntrack(item.link);
                    safeUntrack(item.linkedRange);
                }

                const translationRequests = [];
                const itemsToProcess = [];
                let reqId = 0;

                const mixedWordShapesSet = new Set(mixedWordItems.map(m => m.textRange));

                for (const shape of shapesToProcess) {
                    if (isTableShape(shape.type)) {
                        const cellEntries = tableCellsMap.get(shape) || [];
                        console.log(`[SlideTranslation] Gathering text from ${cellEntries.length} cells in table shape...`);
                        for (const entry of cellEntries) {
                            const { cell, table, row, col } = entry;
                            if (!cell || cell.isNullObject) continue;
                            let originalText = "";
                            try {
                                originalText = cell.text;
                            } catch (e) {
                                console.warn(`[SlideTranslation] Error reading cell text at [${row}, ${col}]:`, e);
                                continue;
                            }

                            if (originalText && originalText.trim().length > 0) {
                                const currentId = reqId++;

                                // Detect hyperlinks associated with this table cell
                                const cellLinks = [];
                                for (const sEntry of slideLinkLoadEntries) {
                                    if (!sEntry.linkedRange || sEntry.linkedRange.isNullObject) continue;
                                    const lText = sEntry.linkedRange.text;
                                    if (!lText) continue;

                                    const shapeMatches = sEntry.linkedShape && !sEntry.linkedShape.isNullObject &&
                                        (sEntry.linkedShape.id === shape.id || isTableShape(sEntry.linkedShape.type));

                                    if ((shapeMatches && originalText.includes(lText)) || originalText.trim() === lText.trim()) {
                                        console.log(`[SlideTranslation] Matched hyperlink to Table Cell [${row}, ${col}]: "${lText}" -> ${sEntry.link.address}`);
                                        cellLinks.push({
                                            address: sEntry.link.address,
                                            screenTip: sEntry.link.screenTip,
                                            text: lText,
                                            linkedRange: sEntry.linkedRange,
                                            link: sEntry.link
                                        });
                                    }
                                }

                                try {
                                    if (cell.hyperlinks && cell.hyperlinks.items && cell.hyperlinks.items.length > 0) {
                                        for (const cLink of cell.hyperlinks.items) {
                                            cellLinks.push({
                                                address: cLink.address,
                                                screenTip: cLink.screenTip,
                                                text: originalText,
                                                link: cLink
                                            });
                                        }
                                    }
                                } catch (e) { }

                                const urlRegex = /(https?:\/\/[^\s]+)/gi;
                                const urlMatches = originalText.match(urlRegex);
                                if (urlMatches && cellLinks.length === 0) {
                                    for (const u of urlMatches) {
                                        cellLinks.push({
                                            address: u,
                                            screenTip: "",
                                            text: u
                                        });
                                    }
                                }

                                console.log(`[SlideTranslation] Queued Table Cell [${row}, ${col}] (ID ${currentId}): "${originalText}" (Links: ${cellLinks.length})`);
                                translationRequests.push({
                                    id: currentId,
                                    text: originalText
                                });
                                itemsToProcess.push({
                                    type: "cell",
                                    cell,
                                    table,
                                    shape,
                                    row,
                                    col,
                                    reqId: currentId,
                                    originalText,
                                    cellLinks
                                });
                            }
                        }
                    } else if (!isNonTextFrameShape(shape.type, shape.name) && shape.textFrame && shape.textFrame.hasText) {
                        const textRange = shape.textFrame.textRange;
                        const originalText = textRange.text;
                        if (!originalText || originalText.trim().length === 0) continue;

                        const font = textRange.font;
                        const hAlign = textRange.paragraphFormat.horizontalAlignment;

                        const paragraphInfos = shapeParagraphsMap.get(textRange) || [];
                        const paragraphBullets = paragraphInfos.map(p => {
                            let bType = null;
                            let bStyle = null;
                            let vis = false;
                            let indent = 0;
                            let hAlign = null;
                            try {
                                if (p.range && p.range.paragraphFormat) {
                                    indent = p.range.paragraphFormat.indentLevel || 0;
                                    hAlign = p.range.paragraphFormat.horizontalAlignment;
                                    if (p.range.paragraphFormat.bulletFormat) {
                                        bType = p.range.paragraphFormat.bulletFormat.type;
                                        bStyle = p.range.paragraphFormat.bulletFormat.style;
                                        vis = p.range.paragraphFormat.bulletFormat.visible;
                                    }
                                }
                            } catch (e) { }
                            // Untrack substring paragraph range after loading bullet formatting
                            safeUntrack(p.range);
                            return { type: bType, style: bStyle, visible: vis, indentLevel: indent, hAlign: hAlign };
                        });

                        console.log("[Debug] Original Shape Gather:", {
                            shapeType: shape.type,
                            originalTextRaw: originalText,
                            originalTextJSON: JSON.stringify(originalText),
                            paragraphBullets: paragraphBullets,
                            isUniform: !mixedWordShapesSet.has(textRange)
                        });

                        if (!mixedWordShapesSet.has(textRange)) {
                            // Uniform text range!
                            const currentId = reqId++;
                            translationRequests.push({
                                id: currentId,
                                text: originalText
                            });
                            itemsToProcess.push({
                                type: "uniform",
                                textRange,
                                originalText,
                                reqId: currentId,
                                fName: font.name,
                                fSize: font.size,
                                fColor: font.color,
                                fBold: font.bold,
                                fItalic: font.italic,
                                fUnderline: font.underline,
                                hAlign,
                                paragraphBullets
                            });
                        } else {
                            // Mixed text range!
                            const mixedItem = mixedWordItems.find(m => m.textRange === textRange);
                            const words = mixedItem.words;
                            const charHyperlinks = shapeHyperlinksMap.get(textRange) || new Array(originalText.length).fill(null);

                            const runs = [];
                            let currentRun = null;

                            const isSameHyperlink = (h1, h2) => {
                                if (!h1 && !h2) return true;
                                if (!h1 || !h2) return false;
                                return h1.address === h2.address && h1.screenTip === h2.screenTip;
                            };

                            for (const item of words) {
                                if (!item.isWord) {
                                    if (currentRun) {
                                        currentRun.text += item.text;
                                    } else {
                                        currentRun = {
                                            text: item.text,
                                            fName: font.name || "Arial",
                                            fSize: font.size || 12,
                                            fColor: font.color || "#000000",
                                            fBold: font.bold || false,
                                            fItalic: font.italic || false,
                                            fUnderline: font.underline || false,
                                            hyperlink: charHyperlinks[item.start]
                                        };
                                    }
                                    continue;
                                }

                                let wName, wSize, wColor, wBold, wItalic, wUnderline;
                                try {
                                    wName = item.range.font.name;
                                    wSize = item.range.font.size;
                                    wColor = item.range.font.color;
                                    wBold = item.range.font.bold;
                                    wItalic = item.range.font.italic;
                                    wUnderline = item.range.font.underline;
                                } catch (e) {
                                    wName = font.name || "Arial";
                                    wSize = font.size || 12;
                                    wColor = font.color || "#000000";
                                    wBold = font.bold || false;
                                    wItalic = font.italic || false;
                                    wUnderline = font.underline || false;
                                }

                                safeUntrack(item.range);

                                let wHyperlink = null;
                                for (let idx = item.start; idx < item.start + item.length; idx++) {
                                    if (charHyperlinks[idx]) {
                                        wHyperlink = charHyperlinks[idx];
                                        break;
                                    }
                                }

                                if (currentRun &&
                                    currentRun.fName === wName &&
                                    currentRun.fSize === wSize &&
                                    currentRun.fColor === wColor &&
                                    currentRun.fBold === wBold &&
                                    currentRun.fItalic === wItalic &&
                                    currentRun.fUnderline === wUnderline &&
                                    isSameHyperlink(currentRun.hyperlink, wHyperlink)) {
                                    currentRun.text += item.text;
                                } else {
                                    if (currentRun) {
                                        runs.push(currentRun);
                                    }
                                    currentRun = {
                                        text: item.text,
                                        fName: wName,
                                        fSize: wSize,
                                        fColor: wColor,
                                        fBold: wBold,
                                        fItalic: wItalic,
                                        fUnderline: wUnderline,
                                        hyperlink: wHyperlink
                                    };
                                }
                            }
                            if (currentRun) {
                                runs.push(currentRun);
                            }

                            const runReqs = [];
                            for (const run of runs) {
                                if (run.text && run.text.trim().length > 0) {
                                    const currentId = reqId++;
                                    translationRequests.push({
                                        id: currentId,
                                        text: run.text
                                    });
                                    runReqs.push({
                                        run,
                                        reqId: currentId
                                    });
                                } else {
                                    runReqs.push({
                                        run,
                                        reqId: null
                                    });
                                }
                            }

                            console.log("[Debug] Mixed Shape Runs Gathered:", {
                                runsCount: runs.length,
                                runs: runs.map(r => ({ textJSON: JSON.stringify(r.text), hyperlink: r.hyperlink }))
                            });

                            itemsToProcess.push({
                                type: "mixed",
                                textRange,
                                originalText,
                                runReqs,
                                hAlign,
                                paragraphBullets
                            });
                        }
                    }
                }
                console.timeEnd("Phase 1: Batched Text & Format Gathering");

                console.time("Phase 2: Translation API Calls");
                checkCancellation();
                const translationMap = {};
                const sourceTexts = translationRequests.map(r => r.text);
                const translatedTexts = await translateTextArray(sourceTexts, targetLanguage, "all", isCancelledRef);
                checkCancellation();

                for (let j = 0; j < translationRequests.length; j++) {
                    const req = translationRequests[j];
                    const translated = (translatedTexts && j < translatedTexts.length) ? translatedTexts[j] : req.text;
                    translationMap[req.id] = translated;
                    console.log(`[Debug] API Translation [ID ${req.id}]:`, {
                        inJSON: JSON.stringify(req.text),
                        outJSON: JSON.stringify(translated)
                    });
                }
                console.timeEnd("Phase 2: Translation API Calls");

                console.time("Phase 3: Write Translated Text & Styles");

                // --- Phase 3a: Table Cells ---
                const tableItems = itemsToProcess.filter(it => it.type === "cell");
                if (tableItems.length > 0) {
                    console.log(`[SlideTranslation] Phase 3a: Writing ${tableItems.length} table cells...`);
                    for (const item of tableItems) {
                        let translatedText = translationMap[item.reqId];
                        if (translatedText) {
                            translatedText = translatedText.replace(/\r\n/g, "\r").replace(/\n/g, "\r");
                        } else {
                            translatedText = item.originalText;
                        }
                        console.log(`[SlideTranslation] Setting Table Cell [${item.row}, ${item.col}] text: "${item.originalText}" -> "${translatedText}"`);

                        const targetCell = item.table ? item.table.getCellOrNullObject(item.row, item.col) : item.cell;
                        const cellLinks = item.cellLinks || [];

                        if (cellLinks.length > 0) {
                            console.log(`[SlideTranslation] Cell [${item.row}, ${item.col}] has ${cellLinks.length} hyperlink(s):`, cellLinks.map(l => l.address));
                            let updatedViaRange = false;

                            // Strategy 1: If 1 link whose original text equals cell text, try updating linkedRange directly
                            if (cellLinks.length === 1 && cellLinks[0].linkedRange && !cellLinks[0].linkedRange.isNullObject) {
                                const lRange = cellLinks[0].linkedRange;
                                if (cellLinks[0].text.trim() === item.originalText.trim()) {
                                    try {
                                        console.log(`[SlideTranslation] Cell [${item.row}, ${item.col}]: Updating linkedRange.text directly to preserve hyperlink...`);
                                        lRange.text = translatedText;
                                        updatedViaRange = true;
                                    } catch (err) {
                                        console.warn(`[SlideTranslation] Cell [${item.row}, ${item.col}]: Setting linkedRange.text failed:`, err);
                                    }
                                }
                            }

                            // Strategy 2: If linkedRange was not updated directly, set targetCell.text
                            if (!updatedViaRange) {
                                try {
                                    targetCell.text = translatedText;
                                } catch (e) {
                                    console.warn(`[SlideTranslation] Error setting cell [${item.row}, ${item.col}]:`, e);
                                }
                            }

                            // Strategy 3: Reapply hyperlinks using all available PowerPoint APIs
                            for (const linkInfo of cellLinks) {
                                if (typeof targetCell.setHyperlink === "function") {
                                    try {
                                        console.log(`[SlideTranslation] Calling targetCell.setHyperlink on cell [${item.row}, ${item.col}] -> ${linkInfo.address}`);
                                        targetCell.setHyperlink({
                                            address: linkInfo.address,
                                            screenTip: linkInfo.screenTip || ""
                                        });
                                    } catch (e) {
                                        console.warn(`[SlideTranslation] targetCell.setHyperlink failed:`, e);
                                    }
                                }
                                if (linkInfo.linkedRange && !linkInfo.linkedRange.isNullObject && typeof linkInfo.linkedRange.setHyperlink === "function") {
                                    try {
                                        console.log(`[SlideTranslation] Calling linkedRange.setHyperlink on cell [${item.row}, ${item.col}] -> ${linkInfo.address}`);
                                        linkInfo.linkedRange.setHyperlink({
                                            address: linkInfo.address,
                                            screenTip: linkInfo.screenTip || ""
                                        });
                                    } catch (e) {
                                        console.warn(`[SlideTranslation] linkedRange.setHyperlink failed:`, e);
                                    }
                                }
                                if (newSlide.hyperlinks && typeof newSlide.hyperlinks.add === "function") {
                                    try {
                                        console.log(`[SlideTranslation] Calling newSlide.hyperlinks.add for [${item.row}, ${item.col}] -> ${linkInfo.address}`);
                                        newSlide.hyperlinks.add(linkInfo.linkedRange || targetCell, {
                                            address: linkInfo.address,
                                            screenTip: linkInfo.screenTip || ""
                                        });
                                    } catch (e) { }
                                }
                                if (targetCell.hyperlinks && typeof targetCell.hyperlinks.add === "function") {
                                    try {
                                        console.log(`[SlideTranslation] Calling targetCell.hyperlinks.add for [${item.row}, ${item.col}] -> ${linkInfo.address}`);
                                        targetCell.hyperlinks.add({
                                            address: linkInfo.address,
                                            screenTip: linkInfo.screenTip || ""
                                        });
                                    } catch (e) { }
                                }
                            }
                        } else {
                            try {
                                targetCell.text = translatedText;
                            } catch (e) {
                                console.warn(`[SlideTranslation] Error setting cell [${item.row}, ${item.col}]:`, e);
                            }
                        }
                    }
                    console.log("[SlideTranslation] Syncing Phase 3a: Applying table cell text and hyperlinks...");
                    await context.sync();
                    console.log("[SlideTranslation] Phase 3a: Table cells synced successfully!");
                }

                // --- Phase 3b: Base Text on Shapes ---
                const uniformItems = itemsToProcess.filter(it => it.type === "uniform");
                const mixedItems = itemsToProcess.filter(it => it.type === "mixed");

                if (uniformItems.length > 0 || mixedItems.length > 0) {
                    console.log(`[SlideTranslation] Phase 3b: Writing base text for ${uniformItems.length} uniform and ${mixedItems.length} mixed shapes...`);

                    for (const item of uniformItems) {
                        let translatedText = translationMap[item.reqId];
                        if (translatedText) {
                            translatedText = translatedText.replace(/\r\n/g, "\r").replace(/\n/g, "\r");
                            if (item.originalText && (item.originalText.endsWith("\r") || item.originalText.endsWith("\n")) && !translatedText.endsWith("\r")) {
                                translatedText += "\r";
                            }
                        } else {
                            translatedText = item.originalText;
                        }
                        item.translatedText = translatedText;
                        console.log("[Debug] Uniform Write:", {
                            originalJSON: JSON.stringify(item.originalText),
                            translatedJSON: JSON.stringify(translatedText),
                            paragraphBullets: item.paragraphBullets
                        });
                        item.textRange.text = translatedText;
                    }

                    for (const item of mixedItems) {
                        let fullTranslatedText = "";
                        const runMappings = [];
                        for (const rr of item.runReqs) {
                            let translatedText = rr.reqId !== null ? translationMap[rr.reqId] : rr.run.text;
                            if (translatedText) {
                                translatedText = translatedText.replace(/\r\n/g, "\r").replace(/\n/g, "\r");
                            } else {
                                translatedText = rr.run.text;
                            }
                            const start = fullTranslatedText.length;
                            fullTranslatedText += translatedText;
                            runMappings.push({
                                start,
                                length: translatedText.length,
                                fName: rr.run.fName,
                                fSize: rr.run.fSize,
                                fColor: rr.run.fColor,
                                fBold: rr.run.fBold,
                                fItalic: rr.run.fItalic,
                                fUnderline: rr.run.fUnderline,
                                hyperlink: rr.run.hyperlink
                            });
                        }
                        if (item.originalText && (item.originalText.endsWith("\r") || item.originalText.endsWith("\n")) && !fullTranslatedText.endsWith("\r")) {
                            fullTranslatedText += "\r";
                        }
                        item.fullTranslatedText = fullTranslatedText;
                        item.runMappings = runMappings;

                        console.log("[Debug] Mixed Write:", {
                            originalJSON: JSON.stringify(item.originalText),
                            fullTranslatedJSON: JSON.stringify(fullTranslatedText),
                            paragraphBullets: item.paragraphBullets
                        });

                        if (item.textRange.hyperlinks && item.textRange.hyperlinks.items) {
                            for (const oldLink of item.textRange.hyperlinks.items) {
                                try { oldLink.delete(); } catch (e) { }
                            }
                        }
                        item.textRange.text = fullTranslatedText;
                    }

                    console.log("[SlideTranslation] Syncing Phase 3b: Committing base text to shapes...");
                    await context.sync();
                    console.log("[SlideTranslation] Phase 3b: Base text committed successfully!");

                    // --- Phase 3c: Formatting & Substring Styles (on committed text) ---
                    let hasFormatting = false;

                    for (const item of uniformItems) {
                        if (item.paragraphBullets && item.paragraphBullets.length > 0) {
                            try {
                                const cleanText = item.translatedText.replace(/\r\n/g, "\r").replace(/\n/g, "\r");
                                let pStart = 0;
                                const lines = cleanText.split("\r");
                                for (let i = 0; i < lines.length; i++) {
                                    const len = lines[i].length;
                                    if (len > 0) {
                                        const subP = item.textRange.getSubstring(pStart, len);
                                        const bInfo = item.paragraphBullets[i] || item.paragraphBullets[item.paragraphBullets.length - 1];
                                        if (bInfo) {
                                            hasFormatting = true;
                                            if (bInfo.indentLevel !== null && bInfo.indentLevel !== undefined) {
                                                try { subP.paragraphFormat.indentLevel = bInfo.indentLevel; } catch (e) { }
                                            }
                                            if (bInfo.hAlign !== null && bInfo.hAlign !== undefined) {
                                                try { subP.paragraphFormat.horizontalAlignment = bInfo.hAlign; } catch (e) { }
                                            }
                                            if (bInfo.type && String(bInfo.type).toLowerCase() !== "none") {
                                                try {
                                                    subP.paragraphFormat.bulletFormat.type = bInfo.type;
                                                    if (bInfo.style) subP.paragraphFormat.bulletFormat.style = bInfo.style;
                                                } catch (e) { }
                                            } else if (bInfo.visible !== null && bInfo.visible !== undefined) {
                                                try { subP.paragraphFormat.bulletFormat.visible = bInfo.visible; } catch (e) { }
                                            }
                                        }
                                    }
                                    pStart += len + 1;
                                }
                            } catch (e) {
                                console.warn("[Debug] Could not re-apply bulletFormat to uniform item:", e);
                            }
                        }
                    }

                    for (const item of mixedItems) {
                        if (item.paragraphBullets && item.paragraphBullets.length > 0) {
                            try {
                                const cleanText = item.fullTranslatedText.replace(/\r\n/g, "\r").replace(/\n/g, "\r");
                                let pStart = 0;
                                const lines = cleanText.split("\r");
                                for (let i = 0; i < lines.length; i++) {
                                    const len = lines[i].length;
                                    if (len > 0) {
                                        const subP = item.textRange.getSubstring(pStart, len);
                                        const bInfo = item.paragraphBullets[i] || item.paragraphBullets[item.paragraphBullets.length - 1];
                                        if (bInfo) {
                                            hasFormatting = true;
                                            if (bInfo.indentLevel !== null && bInfo.indentLevel !== undefined) {
                                                try { subP.paragraphFormat.indentLevel = bInfo.indentLevel; } catch (e) { }
                                            }
                                            if (bInfo.hAlign !== null && bInfo.hAlign !== undefined) {
                                                try { subP.paragraphFormat.horizontalAlignment = bInfo.hAlign; } catch (e) { }
                                            }
                                            if (bInfo.type && String(bInfo.type).toLowerCase() !== "none") {
                                                try {
                                                    subP.paragraphFormat.bulletFormat.type = bInfo.type;
                                                    if (bInfo.style) subP.paragraphFormat.bulletFormat.style = bInfo.style;
                                                } catch (e) { }
                                            } else if (bInfo.visible !== null && bInfo.visible !== undefined) {
                                                try { subP.paragraphFormat.bulletFormat.visible = bInfo.visible; } catch (e) { }
                                            }
                                        }
                                    }
                                    pStart += len + 1;
                                }
                            } catch (e) {
                                console.warn("[Debug] Could not re-apply bulletFormat to mixed item:", e);
                            }
                        }

                        if (item.runMappings && item.runMappings.length > 0) {
                            for (const mapping of item.runMappings) {
                                if (mapping.length > 0) {
                                    hasFormatting = true;
                                    const sub = item.textRange.getSubstring(mapping.start, mapping.length);
                                    if (mapping.fName) sub.font.name = mapping.fName;
                                    if (mapping.fSize) sub.font.size = mapping.fSize;
                                    if (mapping.fColor) sub.font.color = mapping.fColor;
                                    if (mapping.fBold) sub.font.bold = mapping.fBold;
                                    if (mapping.fItalic) sub.font.italic = mapping.fItalic;
                                    if (mapping.fUnderline) sub.font.underline = mapping.fUnderline;
                                    if (mapping.hyperlink) {
                                        sub.setHyperlink({
                                            address: mapping.hyperlink.address,
                                            screenTip: mapping.hyperlink.screenTip
                                        });
                                    }
                                }
                            }
                        }
                    }

                    if (hasFormatting) {
                        console.log("[SlideTranslation] Syncing Phase 3c: Applying substring formats and bullet styles...");
                        await context.sync();
                        console.log("[SlideTranslation] Phase 3c: Formatting synced successfully!");
                    }
                }

                console.timeEnd("Phase 3: Write Translated Text & Styles");

                // Clean up proxy objects from trackedObjects context memory
                for (const sEntry of slideLinkLoadEntries) {
                    if (sEntry.link) safeUntrack(sEntry.link);
                    if (sEntry.linkedRange) safeUntrack(sEntry.linkedRange);
                    if (sEntry.linkedShape) safeUntrack(sEntry.linkedShape);
                }
                for (const [shape, table] of shapeTableMap.entries()) {
                    const cells = tableCellsMap.get(shape) || [];
                    for (const entry of cells) {
                        if (entry && entry.cell) {
                            safeUntrack(entry.cell);
                        }
                    }
                    safeUntrack(table);
                }
                for (const shapeObj of shapesToProcess) {
                    safeUntrack(shapeObj);
                }
                safeUntrack(newSlide);
                safeUntrack(originalSlide);

                return "Success";
            });
        } finally {
            console.timeEnd("Slide Translation");
        }
    };

    return ({
        getPresentationBase64,
        closeActiveOfficeFile,
        replicateAndTranslateSlide,
        translateText,
        translateTextArray,
        addTextBoxToSlide,
        addBackgroundImageToSlide,
        addBackgroundImageToSlideV3,
        getPresentationSlideSize,
        getTextBoxPositions,
        addTextBoxWithStyles,
        getVelazquezMathJournalGraphics,
        getVelazquezMathJournalFrames,
        addVelazquezSlides,
        openLoginDialog,
        insertQuestionSet,
        getGraphicOrganiser,
        getLvl3Filters,
        getBundleLessonPlan,
        lvl3Search,
        addTextBoxesWithStyles,
        getStripeBundlePrices,
        getActiveSubscriptions,
        getValidateCoupon,
        postPaymentToken,
        renewSubscriptionToggle,
        isBrowserPowerpoint,
        initPaymentForBrowser,
        goToPaymentScreen,
        processPaymentMethod,
        getActiveUserEmail,
        isLoggedIn,
        logOut,
        gAPageview,
        gAStuPageview,
        gAGeneralPurchaseEvents,
        gAChangeSubsTypeEvent,
        gAPurchaseButtonSelectedEvent,
        gALessonPlansEvent,
        gAErrorEvent,
        gAStuErrorEvent,
        gABasicEvent,
        gATextBoxAddEvent,
        gAStuTextBoxAddEvent,
        gAGraphicOrganizerAddEvent,
        gALangScaffSearchEvent,
        gAStuLangScaffSearchEvent,
        gAChangeSelectPairsEvent,
        gATextBoxPairAddEvent,
        gAPurchaseInitiatedEvent,
        gAPurchaseCompletedEvent,
        gAPurchaseFailedEvent,
        getSubscriptionData,
        toolUsageCount,
        getLvl4Content,
        getLvl4login,
        lvl4Search,
        setAddonDefaults,
        getLvl6login,
        addTextBoxPairToSlide,
        getLvl6Content,
        lvl6Search,
        getServerUrl,
        captureException
    });
}